<template>
  <div class="admin-semaine">
    <router-link to="/admin" class="back-btn">← Retour au menu admin</router-link>
    <h1 v-motion
        :initial="{ opacity: 0, y: -30 }"
        :enter="{ opacity: 1, y: 0, transition: { duration: 400 } }">
      📆 Semaine exceptionnelle
    </h1>

    <!-- ℹ️ Explication -->
    <div class="explication" v-motion
         :initial="{ opacity: 0, y: 20 }"
         :enter="{ opacity: 1, y: 0, transition: { duration: 400, delay: 50 } }">
      <p>Changez ici <strong>uniquement certaines dates</strong> : fermer un jour, passer un jour Domicile en Salon, changer les horaires ou les villes…</p>
      <ul>
        <li>🗓️ Votre planning hebdomadaire <strong>n'est pas modifié</strong>.</li>
        <li>📌 Les changements ne s'appliquent qu'aux dates choisies.</li>
        <li>🔁 La semaine suivante revient <strong>automatiquement</strong> au planning normal.</li>
      </ul>
    </div>

    <!-- 🗓️ Choix de la semaine -->
    <div class="semaine-selection" v-motion
         :initial="{ opacity: 0, y: 20 }"
         :enter="{ opacity: 1, y: 0, transition: { duration: 400, delay: 100 } }">
      <div class="calendar-card">
        <v-calendar
          ref="calendrier"
          expanded
          locale="fr"
          :first-day-of-week="2"
          :initial-page="pageInitialeCalendrier"
          :attributes="attributsCalendrier"
          @dayclick="onDayClick"
        />
        <p class="calendar-hint">Touchez un jour pour choisir sa semaine.</p>
      </div>

      <div class="semaine-nav">
        <h2>{{ titre }}</h2>
        <div class="nav-buttons">
          <button type="button" class="nav-btn" :disabled="occupe" @click="semainePrecedente">◀ Semaine précédente</button>
          <button type="button" class="nav-btn" :disabled="occupe" @click="semaineSuivante">Semaine suivante ▶</button>
        </div>
        <button v-if="!estSemaineCourante" type="button" class="lien-btn" :disabled="occupe" @click="semaineCourante">
          ↺ Revenir à la semaine en cours
        </button>
      </div>
    </div>

    <!-- 📋 Bilan des annulations : reste affiché jusqu'à ce que l'admin le ferme, même si une
         semaine ne se charge pas -->
    <section v-if="bilanAnnulations" ref="blocBilan" class="bilan-annulations" tabindex="-1"
             aria-labelledby="bilan-titre">
      <button type="button" class="fermer-messages" aria-label="Fermer le bilan des annulations"
              title="Fermer le bilan" :disabled="occupe" @click="fermerBilan">✕</button>
      <h2 id="bilan-titre">📋 Bilan des annulations</h2>
      <!-- À renvoyer : envois en échec complet et échecs du serveur, pour la semaine affichée -->
      <div v-if="relances.length" class="bilan-envoi-echec" role="alert">
        <p v-if="bilanAnnulations.enAttente.length">
          ❌ {{ bilanAnnulations.enAttente.length }} annulation(s) non envoyée(s)
          ({{ bilanAnnulations.erreurEnvoi }}) : ces rendez-vous ne sont peut-être pas annulés.
        </p>
        <p v-if="relances.length > bilanAnnulations.enAttente.length">
          ❌ {{ relances.length - bilanAnnulations.enAttente.length }} annulation(s) en échec (voir ci-dessous).
        </p>
        <p class="bilan-relance-aide">
          « Réessayer » relit d'abord les réservations : seuls les rendez-vous encore en conflit avec
          le planning de leur jour sont renvoyés.
        </p>
        <button type="button" class="action-btn retry-btn" :disabled="verrouille"
                @click="relancerAnnulations">🔄 Réessayer</button>
      </div>
      <ul v-if="lignesBilan.length">
        <li v-for="l in lignesBilan" :key="l.id" :class="'bilan-' + l.statut.toLowerCase()">
          <span aria-hidden="true">{{ l.icone }}</span> {{ l.texte }}<a v-if="l.telephone" :href="lienTelephone(l.telephone)">{{ l.telephone }}</a>
        </li>
      </ul>
    </section>

    <!-- ⏳ Chargement / erreurs -->
    <div v-if="chargement && !jours.length" class="loading">⏳ Chargement de la semaine…</div>

    <div v-else-if="erreurChargement" class="error-message">
      <p>{{ erreurChargement }}</p>
      <button type="button" class="action-btn retry-btn" @click="chargerSemaine()">🔄 Réessayer</button>
    </div>

    <template v-else>
      <div v-if="avertissementVilles" class="warning-message">
        <p>{{ avertissementVilles }}</p>
        <button type="button" class="action-btn retry-btn" :disabled="chargementVilles" @click="chargerVilles">🔄 Réessayer</button>
      </div>

      <!-- 📅 Les 7 jours (tout est verrouillé pendant l'enregistrement) -->
      <fieldset class="jours-fieldset" :disabled="verrouille">
      <div class="jours-grid" :class="{ rechargement: chargement }">
        <div
          v-for="(jour, i) in jours"
          :key="jour.iso"
          class="jour-card"
          :class="[classeEtat(jour), { 'avec-exception': jour.modifie && !jour.passe, passe: jour.passe }]"
          v-motion
          :initial="{ opacity: 0, y: 20 }"
          :enter="{ opacity: 1, y: 0, transition: { duration: 300, delay: i * 50 } }"
        >
          <div class="jour-header">
            <h3>{{ jour.nom }} <span class="jour-date">{{ dateCourte(jour.date) }}</span></h3>
            <div class="badges">
              <span v-if="jour.exception" class="badge badge-exception">⭐ Exception enregistrée</span>
              <span v-if="jour.passe" class="badge badge-passe">Jour passé</span>
            </div>
          </div>

          <p v-if="jour.exception && jour.exception.motif" class="motif-enregistre">📝 {{ jour.exception.motif }}</p>

          <p class="planning-normal"><strong>Planning normal :</strong> {{ resumePlanning(jour.normal) }}</p>
          <p v-if="jour.passe && jour.exception" class="planning-normal">
            <strong>Exception appliquée :</strong> {{ resumePlanning(jour.exception) }}
          </p>

          <p class="nb-resa">
            📋 {{ jour.reservations.length }} réservation{{ jour.reservations.length > 1 ? 's' : '' }} ce jour
          </p>

          <!-- Interrupteur exception -->
          <label v-if="!jour.passe" class="toggle">
            <input type="checkbox" v-model="jour.modifie" :disabled="verrouille" />
            <span class="toggle-slider" aria-hidden="true"></span>
            <span class="toggle-label">Modifier ce jour uniquement (exception)</span>
          </label>

          <p v-if="!jour.passe && !jour.modifie && jour.exception" class="info-retour">
            ↩️ Ce jour <strong>reviendra au planning normal</strong> : l'exception sera supprimée à l'enregistrement.
          </p>

          <!-- ✏️ Éditeur de l'exception -->
          <div v-if="jour.modifie && !jour.passe" class="editeur">
            <div class="etat-selector">
              <button
                v-for="opt in OPTIONS_ETAT"
                :key="opt.valeur"
                type="button"
                class="etat-btn"
                :class="[opt.classe, { active: jour.brouillon.etat === opt.valeur }]"
                :disabled="verrouille"
                @click="choisirEtat(jour, opt.valeur)"
              >
                {{ opt.libelle }}
              </button>
            </div>

            <!-- Plages horaires (sauf Fermé) -->
            <div v-if="jour.brouillon.etat !== 'FERME'" class="bloc">
              <h4>⏰ Plages horaires</h4>
              <div v-for="(plage, p) in jour.brouillon.plages" :key="plage._cle" class="plage-ligne">
                <input type="time" v-model="plage.heure_debut" aria-label="Heure de début" :disabled="verrouille" />
                <span class="fleche">→</span>
                <input type="time" v-model="plage.heure_fin" aria-label="Heure de fin" :disabled="verrouille" />
                <button type="button" class="icon-btn delete-btn" title="Supprimer cette plage"
                        :disabled="verrouille"
                        @click="retirerPlage(jour, p)">🗑</button>
              </div>
              <p v-if="!jour.brouillon.plages.length" class="no-data">Aucune plage horaire.</p>
              <button type="button" class="action-btn add-btn"
                      :disabled="verrouille || jour.brouillon.plages.length >= MAX_PLAGES"
                      @click="ajouterPlage(jour)">➕ Ajouter une plage</button>
            </div>

            <!-- Villes (Domicile uniquement) -->
            <div v-if="jour.brouillon.etat === 'DOMICILE'" class="bloc">
              <h4>📍 Villes desservies</h4>
              <div v-if="jour.brouillon.departements.length" class="villes-list">
                <span v-for="(ville, v) in jour.brouillon.departements" :key="ville.code_postal + '|' + ville.nom" class="ville-chip">
                  {{ ville.nom }} ({{ ville.code_postal }})
                  <button type="button" class="chip-remove" :title="'Retirer ' + ville.nom"
                          :disabled="verrouille"
                          @click="retirerVille(jour, v)">✕</button>
                </span>
              </div>
              <p v-else class="no-data">Aucune ville.</p>
              <select v-model="jour.villeAAjouter" class="ville-select"
                      :disabled="verrouille || jour.brouillon.departements.length >= MAX_VILLES"
                      @change="ajouterVille(jour)">
                <option value="">➕ Ajouter une ville…</option>
                <option v-for="dept in villesProposees(jour)" :key="dept.id" :value="String(dept.id)">
                  {{ dept.nom }} ({{ dept.code_postal }})
                </option>
              </select>
            </div>

            <div class="motif">
              <label :for="'motif-' + jour.iso">Motif</label>
              <input :id="'motif-' + jour.iso" type="text" v-model="jour.brouillon.motif" :maxlength="MAX_MOTIF"
                     :disabled="verrouille" :aria-describedby="'motif-aide-' + jour.iso"
                     placeholder="ex : vacances, formation…" />
              <small :id="'motif-aide-' + jour.iso" class="motif-aide">Reste interne, sauf dans l'e-mail envoyé à une cliente dont le rendez-vous est annulé.</small>
            </div>

            <p v-if="analyses[i].identiqueNormal" class="info-identique">ℹ️ Identique au planning normal.</p>

            <ul v-if="analyses[i].erreurs.length" class="erreurs-jour">
              <li v-for="(msg, e) in analyses[i].erreurs" :key="e">❌ {{ msg }}</li>
            </ul>
          </div>

          <!-- ⚠️ Conflits avec les réservations existantes -->
          <div v-if="analyses[i].conflits.length" class="conflits">
            <strong>⚠️ {{ analyses[i].conflits.length }} réservation{{ analyses[i].conflits.length > 1 ? 's' : '' }} en conflit :</strong>
            <ul>
              <li v-for="c in analyses[i].conflits" :key="c.id">
                {{ c.heure }} — {{ c.client }} ({{ c.mode }}) : {{ c.raison }}
              </li>
            </ul>
            <p v-if="analyses[i].action">
              À l'enregistrement, vous choisirez de les <strong>annuler</strong> (en prévenant les clientes) ou de les garder.
            </p>
            <!-- Jour déjà enregistré qui garde des rendez-vous en conflit : on peut encore les annuler -->
            <button v-else type="button" class="action-btn conflits-btn" :disabled="verrouille"
                    @click="ouvrirAnnulationsDuJour(jour)">
              Annuler ces rendez-vous et prévenir les clientes…
            </button>
          </div>

          <p v-if="analyses[i].action" class="statut-action">{{ libelleAction(jour, analyses[i].action) }}</p>
          <p v-if="erreursSauvegarde[jour.iso]" class="erreur-sauvegarde">❌ {{ erreursSauvegarde[jour.iso] }}</p>
        </div>
      </div>
      </fieldset>

      <!-- 💾 Enregistrement (les messages s'affichent ici : c'est là que se trouve l'admin) -->
      <div class="barre-enregistrement">
        <div v-if="message || erreur || erreurRechargement" class="barre-messages" aria-live="polite">
          <button type="button" class="fermer-messages" title="Masquer ces messages" @click="fermerMessages">✕</button>
          <p v-if="message" class="success-message">{{ message }}</p>
          <p v-if="erreur" class="error-message">{{ erreur }}</p>
          <div v-if="erreurRechargement" class="warning-message">
            <p>{{ erreurRechargement }}</p>
            <button type="button" class="action-btn retry-btn"
                    :disabled="chargement || occupe"
                    @click="rechargerSemaine">🔄 Réessayer</button>
          </div>
        </div>
        <span class="resume-modifs">
          {{ nbJoursModifies ? `${nbJoursModifies} jour(s) à mettre à jour` : 'Aucune modification en attente' }}
        </span>
        <div class="barre-actions">
          <button v-if="nbJoursModifies" type="button" class="action-btn cancel-btn"
                  :disabled="verrouille" @click="annulerModifications">↩️ Annuler</button>
          <button type="button" class="action-btn save-btn"
                  :disabled="occupe || chargement || !nbJoursModifies"
                  @click="enregistrerSemaine">
            {{ occupe ? (progression || '⏳ Enregistrement…') : '💾 Enregistrer la semaine' }}
          </button>
        </div>
      </div>
    </template>

    <!-- ⚠️ Fenêtre « Rendez-vous en conflit » : à l'enregistrement (mode ENREGISTRER) ou depuis la
         carte d'un jour déjà enregistré (mode ANNULER : annulations seules, rien n'est enregistré) -->
    <div v-if="modale" class="modale-fond">
      <div ref="modaleEl" class="modale" role="dialog" aria-modal="true" tabindex="-1"
           aria-labelledby="modale-titre" aria-describedby="modale-explication">
        <h2 id="modale-titre">⚠️ Rendez-vous en conflit</h2>
        <p id="modale-explication" class="modale-explication">
          <template v-if="modale.mode === 'ANNULER'">
            Ces rendez-vous ne rentrent pas dans le planning enregistré pour ce jour. Ceux qui sont cochés
            seront <strong>annulés</strong> ; le planning du jour, lui, ne change pas.
          </template>
          <template v-else>
            Ces rendez-vous ne rentrent plus dans le nouveau planning. Ceux qui sont cochés seront
            <strong>annulés</strong>.
          </template>
          Chaque client(e) reçoit un e-mail avec le motif si son adresse e-mail est connue ; sinon, son
          numéro de téléphone vous est indiqué pour l'appeler.
        </p>

        <section v-for="g in modale.groupes" :key="g.iso" class="modale-jour">
          <h3>{{ g.libelle }}</h3>
          <div class="modale-motif">
            <label :for="'modale-motif-' + g.iso">Motif indiqué aux clientes</label>
            <input v-if="g.motifLie" :id="'modale-motif-' + g.iso" type="text" v-model="g.jour.brouillon.motif"
                   :maxlength="MAX_MOTIF" :aria-describedby="'modale-motif-aide-' + g.iso"
                   placeholder="ex : vacances, formation…" />
            <input v-else :id="'modale-motif-' + g.iso" type="text" v-model="g.motifSeul"
                   :maxlength="MAX_MOTIF" :aria-describedby="'modale-motif-aide-' + g.iso"
                   placeholder="ex : vacances, formation…" />
            <small :id="'modale-motif-aide-' + g.iso">{{ modale.mode === 'ANNULER'
              ? "Facultatif. Pré-rempli avec le motif enregistré pour ce jour : le modifier ici change seulement l'e-mail."
              : g.motifLie
                ? "Facultatif. C'est aussi le motif enregistré pour ce jour."
                : "Facultatif. Ce jour revient au planning normal : ce motif sert seulement à l'e-mail." }}</small>
          </div>
          <ul class="modale-resas">
            <li v-for="c in g.conflits" :key="c.id">
              <label class="modale-resa">
                <input type="checkbox" v-model="c.coche" />
                <span>
                  <strong>{{ c.heure }}</strong> — {{ c.client }} ({{ c.mode }})
                  <span class="modale-raison">{{ c.raison }}</span>
                </span>
              </label>
            </li>
          </ul>
        </section>

        <div class="modale-actions">
          <button type="button" class="action-btn modale-principal" :disabled="!nbCoches"
                  @click="confirmerModale(true)">
            {{ modale.mode === 'ANNULER' ? 'Annuler les rendez-vous cochés' : 'Enregistrer et annuler les rendez-vous cochés' }} ({{ nbCoches }})
            <small>E-mail avec le motif si l'adresse est connue, sinon numéro à appeler.</small>
          </button>
          <button v-if="modale.mode !== 'ANNULER'" type="button" class="action-btn modale-secondaire"
                  @click="confirmerModale(false)">
            Enregistrer sans annuler
            <small>Les rendez-vous restent en place : pensez à prévenir les clientes.</small>
          </button>
          <button type="button" class="action-btn modale-retour" @click="retourModale">Retour</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, nextTick, onMounted, onBeforeUnmount } from "vue";
import { onBeforeRouteLeave } from "vue-router";
import api from "@/services/api";
import { getReservationsPeriode, annulerReservations } from "@/services/AdminService";
import {
  getExceptionsAdmin,
  enregistrerException,
  supprimerException,
} from "@/services/PlanningExceptionService";
import {
  formatDateISO,
  jourSemaineBackend,
  indexerExceptions,
  planningPourDate,
} from "@/utils/planningEffectif";

// #region Fonctions pures (aucun état Vue : faciles à vérifier séparément)
const NOMS_JOURS = ["Lundi", "Mardi", "Mercredi", "Jeudi", "Vendredi", "Samedi", "Dimanche"]; // index = jour_semaine - 1
const NOMS_MOIS = ["janvier", "février", "mars", "avril", "mai", "juin", "juillet", "août", "septembre", "octobre", "novembre", "décembre"];
const LIBELLES_MODE = { SALON: "Salon", DOMICILE: "Domicile" };
// Mêmes limites que le backend (PUT /planning-exception/:date/complet)
const MAX_PLAGES = 10;
const MAX_VILLES = 50;
const MAX_MOTIF = 255;
const HEURE_VALIDE = /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/;

// Dates toujours calculées en heure locale (jamais toISOString, qui décale selon le fuseau)
const ajouterJours = (date, n) => new Date(date.getFullYear(), date.getMonth(), date.getDate() + n);
const lundiDeLaSemaine = (date) => ajouterJours(date, 1 - jourSemaineBackend(date));
const joursDeLaSemaine = (lundi) => Array.from({ length: 7 }, (_, i) => ajouterJours(lundi, i));

const numeroJour = (n) => (n === 1 ? "1er" : String(n));
const dateCourte = (date) => `${numeroJour(date.getDate())} ${NOMS_MOIS[date.getMonth()]}`;

// « Semaine du lundi 9 au dimanche 15 novembre 2026 » (mois / année répétés s'ils changent)
const titreSemaine = (lundi) => {
  const dimanche = ajouterJours(lundi, 6);
  let debut = `lundi ${numeroJour(lundi.getDate())}`;
  if (lundi.getFullYear() !== dimanche.getFullYear()) debut += ` ${NOMS_MOIS[lundi.getMonth()]} ${lundi.getFullYear()}`;
  else if (lundi.getMonth() !== dimanche.getMonth()) debut += ` ${NOMS_MOIS[lundi.getMonth()]}`;
  return `Semaine du ${debut} au dimanche ${dateCourte(dimanche)} ${dimanche.getFullYear()}`;
};

// L'API renvoie des TIME 'HH:MM:SS' ; le backend n'accepte que 'HH:MM'
const hhmm = (t) => String(t ?? "").slice(0, 5);
const enMinutes = (t) => {
  const [h, m] = hhmm(t).split(":").map(Number);
  return h * 60 + m;
};
const libelleMode = (mode) => LIBELLES_MODE[mode] || "—";

let compteurCle = 0; // clé stable pour les v-for des plages en cours d'édition
const plagesEditables = (plages) =>
  (plages || [])
    .map((p) => ({ heure_debut: hhmm(p.heure_debut), heure_fin: hhmm(p.heure_fin) }))
    .sort((a, b) => a.heure_debut.localeCompare(b.heure_debut))
    .map((p) => ({ ...p, _cle: ++compteurCle }));

// Fusionne les plages qui se chevauchent (le backend les refuse dans une exception).
// Le planning normal peut en contenir : en base, le lundi porte 14:00-19:30 ET 16:00-19:15.
// Sans cette fusion, pré-remplir le lundi puis enregistrer échouerait.
// Des plages qui se touchent seulement (12:00-14:00 puis 14:00-18:00) restent séparées.
const fusionnerPlages = (plages) => {
  const triees = (plages || [])
    .map((p) => ({ heure_debut: hhmm(p.heure_debut), heure_fin: hhmm(p.heure_fin) }))
    .filter((p) => HEURE_VALIDE.test(p.heure_debut) && HEURE_VALIDE.test(p.heure_fin))
    .sort((a, b) => enMinutes(a.heure_debut) - enMinutes(b.heure_debut));
  const fusion = [];
  for (const p of triees) {
    const derniere = fusion[fusion.length - 1];
    if (derniere && enMinutes(p.heure_debut) < enMinutes(derniere.heure_fin)) {
      if (enMinutes(p.heure_fin) > enMinutes(derniere.heure_fin)) derniere.heure_fin = p.heure_fin;
    } else {
      fusion.push({ ...p });
    }
  }
  return fusion;
};

// Villes { code_postal, nom } nettoyées et sans doublon
const normaliserVilles = (departements) => {
  const vues = new Set();
  const villes = [];
  for (const d of departements || []) {
    const code_postal = String(d?.code_postal ?? "").trim();
    const nom = String(d?.nom ?? "").trim();
    const cle = `${code_postal}|${nom}`;
    if (!code_postal || !nom || vues.has(cle)) continue;
    vues.add(cle);
    villes.push({ code_postal, nom });
  }
  return villes;
};

// Brouillon éditable d'un jour. etat : 'FERME' | 'SALON' | 'DOMICILE'
// Pré-rempli depuis le planning normal ; un jour normalement fermé démarre en Salon sans plage.
const brouillonDepuisNormal = (normal) => {
  if (!normal || !normal.actif) return { etat: "SALON", plages: [], departements: [], motif: "" };
  const domicile = normal.mode === "DOMICILE";
  return {
    etat: domicile ? "DOMICILE" : "SALON",
    plages: plagesEditables(fusionnerPlages(normal.plages)),
    departements: domicile ? normaliserVilles(normal.departements) : [],
    motif: "",
  };
};

// Brouillon depuis une exception enregistrée. Pour un jour fermé, on garde en réserve
// les plages / villes du planning normal : utiles si l'admin rouvre le jour.
const brouillonDepuisException = (exc, normal) => {
  if (!exc.actif) return { ...brouillonDepuisNormal(normal), etat: "FERME", motif: exc.motif || "" };
  const domicile = exc.mode === "DOMICILE";
  return {
    etat: domicile ? "DOMICILE" : "SALON",
    plages: plagesEditables(exc.plages),
    departements: domicile ? normaliserVilles(exc.departements) : [],
    motif: exc.motif || "",
  };
};

// Planning normal vu comme un brouillon (pour savoir si une exception y est identique)
const brouillonEquivalentNormal = (normal) =>
  !normal || !normal.actif
    ? { etat: "FERME", plages: [], departements: [], motif: "" }
    : brouillonDepuisNormal(normal);

// Empreinte de ce qui serait réellement enregistré (sert à détecter les changements)
const signature = (b, avecMotif = true) => {
  const motif = avecMotif ? (b.motif || "").trim() : "";
  if (b.etat === "FERME") return JSON.stringify({ etat: "FERME", motif });
  return JSON.stringify({
    etat: b.etat,
    plages: (b.plages || []).map((p) => `${hhmm(p.heure_debut)}-${hhmm(p.heure_fin)}`).sort(),
    departements: b.etat === "DOMICILE"
      ? normaliserVilles(b.departements).map((d) => `${d.code_postal}|${d.nom}`).sort()
      : [],
    motif,
  });
};

// Corps de PUT /planning-exception/:date/complet
const construirePayload = (b) => {
  const motif = (b.motif || "").trim(); // vide → NULL côté backend
  if (b.etat === "FERME") return { actif: false, motif };
  const payload = {
    actif: true,
    mode: b.etat,
    motif,
    plages: (b.plages || [])
      .map((p) => ({ heure_debut: hhmm(p.heure_debut), heure_fin: hhmm(p.heure_fin) }))
      .sort((x, y) => x.heure_debut.localeCompare(y.heure_debut)),
  };
  if (b.etat === "DOMICILE") payload.departements = normaliserVilles(b.departements);
  return payload;
};

// Mêmes règles que le backend, avec des messages par jour
const validerBrouillon = (b) => {
  const erreurs = [];
  if ((b.motif || "").trim().length > MAX_MOTIF) erreurs.push(`Le motif ne doit pas dépasser ${MAX_MOTIF} caractères.`);
  if (b.etat === "FERME") return erreurs;

  const plages = b.plages || [];
  if (!plages.length) erreurs.push("Ajoutez au moins une plage horaire (ou choisissez « Fermé »).");
  if (plages.length > MAX_PLAGES) erreurs.push(`${MAX_PLAGES} plages horaires maximum.`);
  plages.forEach((p, i) => {
    const debut = hhmm(p.heure_debut);
    const fin = hhmm(p.heure_fin);
    if (!debut || !fin) erreurs.push(`Plage ${i + 1} : indiquez l'heure de début et l'heure de fin.`);
    else if (!HEURE_VALIDE.test(debut) || !HEURE_VALIDE.test(fin)) erreurs.push(`Plage ${i + 1} : horaire invalide.`);
    else if (enMinutes(debut) >= enMinutes(fin)) erreurs.push(`Plage ${i + 1} : l'heure de début doit être avant l'heure de fin.`);
  });

  // Même règle que le backend : des plages qui se chevauchent proposeraient deux fois
  // les mêmes créneaux. Se toucher (12:00-14:00 puis 14:00-18:00) est autorisé.
  const valides = plages
    .map((p) => ({ debut: hhmm(p.heure_debut), fin: hhmm(p.heure_fin) }))
    .filter((p) => HEURE_VALIDE.test(p.debut) && HEURE_VALIDE.test(p.fin) && enMinutes(p.debut) < enMinutes(p.fin))
    .sort((a, b) => enMinutes(a.debut) - enMinutes(b.debut));
  for (let i = 1; i < valides.length; i++) {
    if (enMinutes(valides[i].debut) < enMinutes(valides[i - 1].fin)) {
      erreurs.push(`Les plages ${valides[i - 1].debut}–${valides[i - 1].fin} et ${valides[i].debut}–${valides[i].fin} se chevauchent : fusionnez-les ou corrigez-les.`);
    }
  }

  if (b.etat === "DOMICILE") {
    const villes = normaliserVilles(b.departements);
    if (!villes.length) erreurs.push("Mode Domicile : ajoutez au moins une ville desservie.");
    if (villes.length > MAX_VILLES) erreurs.push(`${MAX_VILLES} villes maximum.`);
  }
  return erreurs;
};

// Ce que l'enregistrement fera pour ce jour : 'ENREGISTRER', 'SUPPRIMER' ou null
const actionDuJour = (jour) => {
  if (jour.passe) return null;
  if (jour.modifie) {
    if (!jour.exception) return "ENREGISTRER";
    return signature(jour.brouillon) !== signature(brouillonDepuisException(jour.exception, jour.normal))
      ? "ENREGISTRER"
      : null;
  }
  return jour.exception ? "SUPPRIMER" : null;
};

// Planning qui s'appliquera à ce jour après enregistrement
const planningCible = (jour) => {
  if (jour.modifie) {
    const b = jour.brouillon;
    if (b.etat === "FERME") return { actif: false, mode: null, plages: [], departements: [] };
    return {
      actif: true,
      mode: b.etat,
      plages: b.plages || [],
      departements: b.etat === "DOMICILE" ? b.departements || [] : [],
    };
  }
  const n = jour.normal;
  return n && n.actif
    ? { actif: true, mode: n.mode, plages: n.plages || [], departements: n.departements || [] }
    : { actif: false, mode: null, plages: [], departements: [] };
};

// Nom de ville comparable : sans accents, sans majuscules, sans espaces ni tirets / apostrophes
// (« Limogne-en-Quercy » = « limogne en quercy »)
const cleVille = (nom) =>
  String(nom ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");

// La réservation à domicile reste-t-elle desservie ? On compare la ville de la réservation
// aux villes choisies ; sans ville enregistrée (anciennes réservations), on retombe sur le
// département, comme le backend (code postal qui commence par le département).
const villeDesservie = (r, departements) => {
  const ville = cleVille(r.ville);
  if (ville) return (departements || []).some((d) => cleVille(d.nom) === ville);
  const dept = String(r.departement ?? "").trim();
  if (!dept) return true; // rien pour en juger
  return (departements || []).some((d) => String(d.code_postal ?? "").startsWith(dept));
};

// La réservation tient-elle dans les plages ? Même règle que le backend quand il déplace un
// rendez-vous : [début, début + durée totale] doit tenir entièrement dans UNE plage.
// Sans durée connue, on se contente de vérifier l'heure de début.
const tientDansLesPlages = (r, plages) => {
  const debut = enMinutes(r.heure_debut);
  if (Number.isNaN(debut)) return true;
  const duree = Number(r.duree_totale_minutes);
  const avecDuree = Number.isFinite(duree) && duree > 0;
  return plages.some((p) => {
    const pDebut = enMinutes(p.heure_debut);
    const pFin = enMinutes(p.heure_fin);
    return avecDuree
      ? debut >= pDebut && debut + duree <= pFin
      : debut >= pDebut && debut < pFin;
  });
};

// Rendez-vous déjà commencé (ou passé) à l'instant « maintenant » (heure locale de l'admin) :
// il a lieu ou a eu lieu, il n'y a plus rien à annuler (le backend le refuserait d'ailleurs).
// Sans jour connu, on ne peut pas en juger : il reste proposé.
const dejaCommence = (r, maintenant) => {
  if (!maintenant || !r || !r.jour) return false;
  const jour = String(r.jour).slice(0, 10);
  const aujourdhui = formatDateISO(maintenant);
  if (jour !== aujourdhui) return jour < aujourdhui;
  const debut = enMinutes(r.heure_debut);
  if (Number.isNaN(debut)) return false;
  return debut <= maintenant.getHours() * 60 + maintenant.getMinutes();
};

// Réservations existantes incompatibles avec le planning visé. Elles ne sont annulées que si
// l'admin le choisit. code : raison envoyée au backend avec l'annulation
// (POST /admin/reservations/annulations), qui choisit la phrase de l'e-mail au client :
// 'FERME' | 'SALON_UNIQUEMENT' | 'DOMICILE_UNIQUEMENT' | 'VILLE' | 'HORAIRES'.
// maintenant (Date, facultatif) : les rendez-vous déjà commencés à cet instant sont ignorés
// (aujourd'hui, celui de 10:00 n'est plus en conflit à 10:00 : il a lieu).
const conflitsReservations = (cible, reservations, maintenant = null) => {
  const plagesValides = (cible.plages || []).filter(
    (p) => HEURE_VALIDE.test(hhmm(p.heure_debut)) && HEURE_VALIDE.test(hhmm(p.heure_fin))
  );
  const conflits = [];
  for (const r of reservations || []) {
    if (dejaCommence(r, maintenant)) continue;
    const dept = String(r.departement ?? "").trim();
    let raison = null;
    let code = null;
    if (!cible.actif) {
      raison = "le jour sera fermé";
      code = "FERME";
    } else if (r.mode && r.mode !== cible.mode) {
      raison = `le jour sera en ${libelleMode(cible.mode)}`;
      code = cible.mode === "SALON" ? "SALON_UNIQUEMENT" : "DOMICILE_UNIQUEMENT";
    } else if (cible.mode === "DOMICILE" && !villeDesservie(r, cible.departements)) {
      raison = `ville non desservie (${String(r.ville ?? "").trim() || "dépt " + dept})`;
      code = "VILLE";
    } else if (plagesValides.length && r.heure_debut && !tientDansLesPlages(r, plagesValides)) {
      raison = "en dehors des nouveaux horaires";
      code = "HORAIRES";
    }
    if (raison) {
      conflits.push({
        id: r.id,
        heure: hhmm(r.heure_debut),
        client: `${r.prenom || ""} ${r.nom || ""}`.trim() || "Cliente",
        mode: libelleMode(r.mode),
        raison,
        code,
      });
    }
  }
  return conflits;
};

const analyserJour = (jour, maintenant = null) => {
  if (jour.passe) return { action: null, erreurs: [], conflits: [], identiqueNormal: false };
  const action = actionDuJour(jour);
  return {
    action,
    erreurs: jour.modifie ? validerBrouillon(jour.brouillon) : [],
    // Avertir seulement si le jour change (ou reste en exception) : sinon c'est le planning normal
    conflits: jour.modifie || action ? conflitsReservations(planningCible(jour), jour.reservations, maintenant) : [],
    identiqueNormal: jour.modifie &&
      signature(jour.brouillon, false) === signature(brouillonEquivalentNormal(jour.normal), false),
  };
};

const resumePlanning = (p) => {
  if (!p || !p.actif) return "Fermé";
  const plages = (p.plages || [])
    .map((x) => `${hhmm(x.heure_debut)}–${hhmm(x.heure_fin)}`)
    .sort()
    .join(", ") || "aucune plage";
  if (p.mode === "DOMICILE") {
    const villes = normaliserVilles(p.departements).map((v) => v.nom).join(", ") || "aucune ville";
    return `🏠 Domicile · ${plages} · ${villes}`;
  }
  return `🏛️ Salon · ${plages}`;
};

// Réservations d'une période (GET /admin/reservations?debut&fin) rangées par jour 'YYYY-MM-DD'
const grouperReservationsParJour = (reservations) => {
  const parJour = {};
  for (const r of reservations || []) {
    if (!r || !r.jour) continue;
    const iso = String(r.jour).slice(0, 10);
    if (!parJour[iso]) parJour[iso] = [];
    parJour[iso].push(r);
  }
  return parJour;
};

// Exception telle qu'enregistrée : celle renvoyée par le backend (PUT …/complet), sinon
// reconstruite depuis ce qui a été envoyé. Sert à garder un écran juste même si le
// rechargement qui suit l'enregistrement échoue.
const exceptionEnregistree = (iso, payload, reponse) => {
  const exc = reponse && reponse.exception;
  if (exc && typeof exc === "object" && exc.date) return exc;
  return {
    date: iso,
    actif: !!payload.actif,
    mode: payload.actif ? payload.mode : null,
    motif: payload.motif || null,
    plages: payload.actif ? payload.plages || [] : [],
    departements: payload.actif && payload.mode === "DOMICILE" ? payload.departements || [] : [],
  };
};

// Fenêtre « Rendez-vous en conflit » : conflits rangés par jour, tous cochés au départ.
// - À l'enregistrement : un jour qui garde une exception y montre SON motif (motifLie : le
//   modifier dans la fenêtre modifie le jour) ; un jour qui revient au planning normal n'aura
//   plus de motif enregistré : celui de l'e-mail est saisi à part (motifSeul), vide au départ.
// - annulationsSeules (bouton de la carte d'un jour déjà enregistré) : rien n'est enregistré ;
//   le motif de l'e-mail est saisi à part, pré-rempli avec le motif enregistré du jour. Le
//   modifier ne change que l'e-mail, jamais l'exception enregistrée.
const groupesConflits = (aTraiter, annulationsSeules = false) =>
  (aTraiter || [])
    .filter((x) => x.analyse.conflits.length)
    .map(({ jour, analyse }) => ({
      iso: jour.iso,
      libelle: `${jour.nom} ${dateCourte(jour.date)}`,
      jour,
      motifLie: !annulationsSeules && analyse.action === "ENREGISTRER",
      motifSeul: annulationsSeules ? String(jour.exception?.motif ?? "") : "",
      conflits: analyse.conflits.map((c) => ({ ...c, coche: true })),
    }));

// 'YYYY-MM-DD' -> Date locale (null si le format est inattendu)
const dateDepuisIso = (iso) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(iso ?? ""));
  return m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : null;
};

// « jeudi 12 novembre à 09:30 »
const quandRendezVous = (iso, heure) => {
  const date = dateDepuisIso(iso);
  if (!date) return "";
  return `${NOMS_JOURS[jourSemaineBackend(date) - 1].toLowerCase()} ${dateCourte(date)} à ${hhmm(heure)}`;
};

// Élément de POST /admin/reservations/annulations. jour et heure_debut sont ceux affichés à
// l'admin : si le rendez-vous a été déplacé depuis, le backend ne l'annule pas ('deplacee').
// raison = code du conflit ; motif omis s'il est vide.
const requeteAnnulation = (iso, conflit, motif) => {
  const requete = { id: conflit.id, jour: iso, heure_debut: hhmm(conflit.heure) };
  if (conflit.code) requete.raison = conflit.code;
  const m = String(motif ?? "").trim();
  if (m) requete.motif = m;
  return requete;
};

// Rendez-vous cochés à annuler, avec le motif tel qu'il est au moment de la confirmation
// (avant l'enregistrement, qui peut réinitialiser le brouillon d'un jour).
// motifLie : le motif de l'e-mail est celui enregistré pour le jour (« Réessayer » reprendra le
// motif enregistré à ce moment-là) ; sinon c'est un motif saisi pour l'e-mail seulement.
const annulationsCochees = (groupes) =>
  (groupes || []).flatMap((g) => {
    const motif = g.motifLie ? g.jour.brouillon.motif : g.motifSeul;
    return g.conflits
      .filter((c) => c.coche)
      .map((c) => ({
        iso: g.iso,
        quand: quandRendezVous(g.iso, c.heure),
        conflit: c,
        requete: requeteAnnulation(g.iso, c, motif),
        motifLie: !!g.motifLie,
      }));
  });

// Le backend accepte 100 annulations par requête : au-delà (jamais en pratique), plusieurs lots
const MAX_ANNULATIONS_PAR_APPEL = 100;
const lots = (liste, taille) => {
  const paquets = [];
  for (let i = 0; i < (liste || []).length; i += taille) paquets.push(liste.slice(i, i + taille));
  return paquets;
};

// Issues qui ne sont pas des échecs d'annulation (le rendez-vous est annulé, ou il n'y a plus
// rien à annuler)
const STATUTS_SANS_ECHEC = ["annulee", "deplacee", "passee", "introuvable"];

// L'annulation a échoué côté backend pour ce rendez-vous ('erreur', réponse incomplète, statut
// inconnu) : « Réessayer » peut la renvoyer. Les issues locales (nonTraite, local) n'en sont pas.
const estEchec = (issue) =>
  !!issue && !issue.nonTraite && !issue.local && !STATUTS_SANS_ECHEC.includes(issue.statut);

// Ligne du bilan pour un rendez-vous coché. issue : un résultat du backend
// ({ statut, emailClient, telephone, actuel, message, renvoi? }) ou une issue locale (rien envoyé) :
// - { nonTraite: true } : jour non enregistré, rendez-vous laissé en place ;
// - { local: 'PLUS_EN_CONFLIT' } : n'est plus en conflit avec le planning de son jour ;
// - { local: 'GARDE' } : reproposé dans la fenêtre des conflits, l'admin l'a gardé ;
// - { local: 'ABSENT' } : plus à cette date d'après les réservations relues (annulé ou déplacé :
//   on ne sait pas lequel, rien n'a été demandé au serveur) ;
// - { local: 'HORS_SEMAINE', incertain } : à renvoyer, mais l'admin a changé de semaine ; incertain :
//   l'envoi précédent a pu être traité sans que la réponse arrive (voir envoiIncertain).
// renvoi (résultat d'un « Réessayer » d'un envoi qui a pu être traité) : 'introuvable' veut alors
// dire « probablement annulé par l'envoi précédent », dont la réponse a été perdue.
// statut de la ligne : 'EMAIL' | 'APPELER' | 'INCONNU' | 'DEPLACEE' | 'PASSEE' | 'DEJA' | 'PROBABLE'
// | 'ECHEC' | 'NON_TRAITE' | 'PLUS_EN_CONFLIT' | 'GARDE' | 'ABSENT' | 'HORS_SEMAINE'
const ligneBilan = (annulation, issue) => {
  const c = annulation.conflit;
  const ligne = (statut, icone, detail, telephone = null) =>
    ({ id: c.id, statut, icone, texte: `${c.client} — ${annulation.quand} : ${detail}`, telephone });
  const motifEchec = (r) => String(r?.message ?? "").trim() || "erreur serveur";
  if (issue?.nonTraite) {
    return ligne("NON_TRAITE", "⏸️", "pas annulé, car ce jour n'a pas pu être enregistré");
  }
  switch (issue?.local) {
    case "PLUS_EN_CONFLIT":
      return ligne("PLUS_EN_CONFLIT", "ℹ️", "n'est plus en conflit avec le planning, non annulé");
    case "GARDE":
      return ligne("GARDE", "ℹ️", "gardé(e) à votre demande, non annulé");
    case "ABSENT":
      return ligne("ABSENT", "⚠️", "n'est plus à cette date (annulé ou déplacé) — vérifiez");
    // Plus de « Réessayer » ni de bouton garanti sur la carte (un jour revenu au planning normal
    // n'en a pas) : la page Réservations, qui permet toujours d'annuler
    case "HORS_SEMAINE":
      return issue.incertain
        ? ligne("HORS_SEMAINE", "⚠️", "peut-être déjà annulé par l'envoi précédent — vérifiez la copie reçue par e-mail (elle indique s'il faut appeler la cliente), sinon annulez depuis la page Réservations")
        : ligne("HORS_SEMAINE", "⚠️", "non annulé (envoi échoué) — à vérifier et annuler si besoin depuis la page Réservations");
    default:
      break;
  }
  const r = issue || {};
  switch (r.statut) {
    case "annulee": {
      if (r.emailClient === true) return ligne("EMAIL", "✅", "prévenu(e) par e-mail");
      if (r.emailClient === false) {
        const tel = String(r.telephone ?? "").trim();
        return tel
          ? ligne("APPELER", "📞", "pas d'e-mail, à appeler au ", tel)
          : ligne("APPELER", "📞", "pas d'e-mail ni de téléphone enregistré : à prévenir vous-même");
      }
      // Réponse sans emailClient : on ne sait pas si l'e-mail est parti
      return ligne("INCONNU", "⚠️", "annulé, mais l'envoi d'un e-mail n'est pas confirmé : vérifiez que la personne est prévenue");
    }
    case "deplacee": {
      const ou = r.actuel ? quandRendezVous(r.actuel.jour, r.actuel.heure_debut) : "";
      return ligne("DEPLACEE", "⚠️", `déplacé(e) entre-temps${ou ? " au " + ou : ""}, non annulé — vérifiez`);
    }
    case "passee":
      return ligne("PASSEE", "⏱️", "déjà passé, non annulé");
    case "introuvable":
      return r.renvoi
        ? ligne("PROBABLE", "⚠️", "probablement annulé lors de l'envoi précédent — vérifiez la copie reçue par e-mail (elle indique s'il faut appeler la cliente)")
        : ligne("DEJA", "ℹ️", "déjà annulé");
    default:
      return ligne("ECHEC", "❌", `annulation impossible (${motifEchec(r)}) — réessayez, ou annulez depuis la page Réservations`);
  }
};

// Bilan des annulations : { annulations (dans l'ordre), issues: { id: issue }, enAttente,
// erreurEnvoi }. Chaque annulation a soit une issue, soit est en attente : son envoi a échoué
// en entier (réseau, 429, 500…). « Réessayer » reprend celles en attente et les échecs du backend
// (voir annulationsARelancer), après les avoir revérifiées (voir trierRelances).
// envoi : { resultats: { id: résultat }, restants: [annulations non confirmées], message } ;
// un restant marqué incertain a pu être traité par le serveur (voir peutEtreTraite).
// envoyees : annulations envoyées ; si ce sont des annulations reconstruites par « Réessayer »,
// elles remplacent les anciennes (même id) dans le bilan.
// incertaines : ids dont un envoi précédent a pu être traité sans que la réponse arrive : leur
// résultat est marqué renvoi (voir ligneBilan) ; en attente, ils restent incertains.
// Les annulations en attente sont des copies { ...annulation, incertain } (voir envoiIncertain).
const bilanApresEnvoi = (bilan, envoyees, envoi, incertaines = new Set()) => {
  const restants = new Set((envoi.restants || []).map((a) => a.conflit.id));
  const parId = new Map((envoyees || []).map((a) => [a.conflit.id, a]));
  const issues = { ...bilan.issues };
  for (const a of envoyees || []) {
    const id = a.conflit.id;
    if (restants.has(id)) {
      delete issues[id]; // de nouveau en attente : plus d'ancienne issue affichée
      continue;
    }
    const r = envoi.resultats?.[id] || { statut: "erreur", message: "réponse incomplète du serveur", reponseManquante: true };
    issues[id] = incertaines.has(id) ? { ...r, renvoi: true } : r;
  }
  const enAttente = [
    ...(bilan.enAttente || []).filter((a) => !parId.has(a.conflit.id)),
    ...(envoi.restants || []).map((a) => ({ ...a, incertain: !!a.incertain || incertaines.has(a.conflit.id) })),
  ];
  let erreurEnvoi = "";
  if (enAttente.length) erreurEnvoi = (restants.size && envoi.message) || bilan.erreurEnvoi || "erreur réseau ou serveur";
  return {
    ...bilan,
    annulations: (bilan.annulations || []).map((a) => parId.get(a.conflit.id) || a),
    issues,
    enAttente,
    erreurEnvoi,
  };
};

// Annulations que « Réessayer » peut renvoyer, dans l'ordre du bilan : envoi en échec complet
// (en attente) et échecs du backend pour ce rendez-vous (F6 : sinon une annulation en 'erreur'
// d'un jour revenu au planning normal ne pourrait plus être refaite d'ici).
const annulationsARelancer = (bilan) => {
  if (!bilan) return [];
  const enAttente = new Set((bilan.enAttente || []).map((a) => a.conflit.id));
  return (bilan.annulations || []).filter(
    (a) => enAttente.has(a.conflit.id) || estEchec(bilan.issues?.[a.conflit.id])
  );
};

// Issues locales (rien n'est envoyé) : elles remplacent les précédentes et ces annulations ne
// sont plus à renvoyer
const bilanAvecIssues = (bilan, issuesLocales) => {
  const ids = new Set(Object.keys(issuesLocales || {}));
  if (!bilan || !ids.size) return bilan;
  const enAttente = bilan.enAttente.filter((a) => !ids.has(String(a.conflit.id)));
  return {
    ...bilan,
    issues: { ...bilan.issues, ...issuesLocales },
    enAttente,
    erreurEnvoi: enAttente.length ? bilan.erreurEnvoi : "",
  };
};

// Planning EN VIGUEUR d'un jour : son exception enregistrée, sinon son planning normal (jamais
// un brouillon pas encore enregistré)
const planningEnVigueur = (jour) => {
  const e = jour.exception;
  if (!e) return planningCible({ modifie: false, normal: jour.normal });
  if (!e.actif) return { actif: false, mode: null, plages: [], departements: [] };
  return {
    actif: true,
    mode: e.mode,
    plages: e.plages || [],
    departements: e.mode === "DOMICILE" ? e.departements || [] : [],
  };
};

// Où en est le rendez-vous d'une annulation, d'après les réservations (relues) des jours affichés :
// { etat: 'ABSENT' } : plus dans la semaine (annulé, ou déplacé hors de la semaine) ;
// { etat: 'DEPLACE', actuel: { jour, heure_debut } } : à un autre jour ou une autre heure ;
// { etat: 'COMMENCE' } : toujours là, déjà commencé à « maintenant » ;
// { etat: 'CONFLIT', conflit } : toujours là, en conflit avec planningDuJour(son jour) ;
// { etat: 'VALABLE' } : toujours là, plus en conflit.
const etatRendezVous = (a, jours, maintenant = null, planningDuJour = planningEnVigueur) => {
  const id = String(a.conflit.id);
  for (const jour of jours || []) {
    const r = (jour.reservations || []).find((x) => String(x.id) === id);
    if (!r) continue;
    const heure = hhmm(r.heure_debut);
    if (jour.iso !== a.iso || heure !== hhmm(a.requete?.heure_debut ?? a.conflit.heure)) {
      return { etat: "DEPLACE", actuel: { jour: jour.iso, heure_debut: heure } };
    }
    if (dejaCommence({ ...r, jour: jour.iso }, maintenant)) return { etat: "COMMENCE" };
    const conflit = conflitsReservations(planningDuJour(jour), [r], maintenant)[0];
    return conflit ? { etat: "CONFLIT", conflit } : { etat: "VALABLE" };
  }
  return { etat: "ABSENT" };
};

// L'envoi précédent de cette annulation a pu être traité sans que la réponse arrive : requête en
// échec complet sans réponse HTTP ou en 5xx (pas en 4xx : voir peutEtreTraite), ou résultat absent
// de la réponse
const envoiIncertain = (bilan, a) =>
  (bilan.enAttente || []).some((x) => x.conflit.id === a.conflit.id && x.incertain) ||
  !!bilan.issues?.[a.conflit.id]?.reponseManquante;

// Annulation à renvoyer dont la semaine n'est plus affichée (voir ligneBilan)
const issueHorsSemaine = (bilan, a) => ({ local: "HORS_SEMAINE", incertain: envoiIncertain(bilan, a) });

// Annulation reconstruite pour « Réessayer » : code du conflit ACTUEL, et motif actuel du jour
// (motif enregistré du jour s'il en dépendait, sinon celui choisi pour l'e-mail)
const annulationRevalidee = (a, jour, conflit) => {
  const motif = a.motifLie && jour.exception ? jour.exception.motif : a.requete?.motif;
  return {
    ...a,
    quand: quandRendezVous(a.iso, conflit.heure),
    conflit: { ...conflit },
    requete: requeteAnnulation(a.iso, conflit, motif),
  };
};

// « Réessayer » ne renvoie jamais une charge utile périmée : chaque annulation à renvoyer est
// revérifiée sur les réservations relues à l'instant et sur le planning EN VIGUEUR de son jour.
// Renvoie { aEnvoyer, issues, gardees, incertaines } :
// - aEnvoyer : rendez-vous toujours à ce jour et à cette heure, pas commencé, toujours en conflit :
//   renvoyé avec le code de conflit actuel et le motif actuel du jour. Rendez-vous qui n'est plus
//   du tout dans la semaine (annulé entre-temps, peut-être par l'envoi précédent, ou déplacé
//   ailleurs) : renvoyé tel quel pour que le backend dise ce qu'il est devenu ; il ne peut pas
//   l'annuler (il n'annule qu'un rendez-vous encore à ce jour et à cette heure) ;
// - issues (rien n'est envoyé) : plus en conflit, déjà commencé, déplacé dans la semaine, jour qui
//   n'est plus affiché ;
// - gardees : jour avec des modifications non enregistrées : laissées en attente (enregistrer ce
//   jour les reproposera dans la fenêtre des conflits) ;
// - incertaines : ids dont l'envoi précédent a pu être traité (voir envoiIncertain).
const trierRelances = (bilan, jours, maintenant = null) => {
  const aEnvoyer = [];
  const issues = {};
  const gardees = [];
  const incertaines = new Set();
  for (const a of annulationsARelancer(bilan)) {
    const id = a.conflit.id;
    const jour = (jours || []).find((j) => j.iso === a.iso);
    if (!jour) {
      issues[id] = issueHorsSemaine(bilan, a);
      continue;
    }
    if (actionDuJour(jour)) {
      gardees.push(a);
      continue;
    }
    const etat = etatRendezVous(a, jours, maintenant);
    if (etat.etat === "CONFLIT" || etat.etat === "ABSENT") {
      if (envoiIncertain(bilan, a)) incertaines.add(id);
      aEnvoyer.push(etat.etat === "CONFLIT" ? annulationRevalidee(a, jour, etat.conflit) : a);
    } else if (etat.etat === "DEPLACE") {
      issues[id] = { statut: "deplacee", actuel: etat.actuel };
    } else if (etat.etat === "COMMENCE") {
      issues[id] = { statut: "passee" };
    } else {
      issues[id] = { local: "PLUS_EN_CONFLIT" };
    }
  }
  return { aEnvoyer, issues, gardees, incertaines };
};

// L'admin vient de redécider des annulations à renvoyer (avant la fusion avec le nouveau bilan) :
// - montrées dans la fenêtre des conflits et cochées : la nouvelle annulation les remplace ;
// - montrées et pas cochées (décochées, « Enregistrer sans annuler ») : gardées, plus à renvoyer ;
// - d'un jour qu'on vient d'enregistrer (PUT ou DELETE de son exception), sans être montrées :
//   plus à renvoyer non plus (la fenêtre les aurait reproposées si elles étaient encore en
//   conflit). Issue d'après les réservations relues avant la fenêtre (absent, déplacé, commencé),
//   sinon jour en échec (nonTraite) ou plus en conflit. Absent de la semaine : annulé OU déplacé
//   ailleurs, rien ne permet de le dire ici (le serveur n'est pas interrogé) : ligne neutre.
// decision : { montres: Set(id), coches: Set(id), isosTouches: Set(iso), isosEchec: Set(iso),
// jours, maintenant }
const bilanApresDecision = (bilan, decision) => {
  if (!bilan) return bilan;
  const {
    montres = new Set(), coches = new Set(), isosTouches = new Set(), isosEchec = new Set(),
    jours = [], maintenant = null,
  } = decision || {};
  const issues = {};
  for (const a of annulationsARelancer(bilan)) {
    const id = a.conflit.id;
    if (coches.has(id)) continue;
    if (montres.has(id)) {
      issues[id] = { local: "GARDE" };
      continue;
    }
    if (!isosTouches.has(a.iso)) continue; // pas concernée : reste à renvoyer
    const etat = etatRendezVous(a, jours, maintenant, planningCible);
    if (etat.etat === "ABSENT") {
      issues[id] = { local: "ABSENT" };
    } else if (etat.etat === "DEPLACE") {
      issues[id] = { statut: "deplacee", actuel: etat.actuel };
    } else if (etat.etat === "COMMENCE") {
      issues[id] = { statut: "passee" };
    } else if (isosEchec.has(a.iso)) {
      issues[id] = { nonTraite: true };
    } else if (etat.etat === "VALABLE") {
      issues[id] = { local: "PLUS_EN_CONFLIT" };
    }
    // CONFLIT sans avoir été montré : impossible (la fenêtre montre tous les conflits des jours
    // enregistrés, calculés sur les mêmes réservations). Par prudence, l'annulation reste à
    // renvoyer : « Réessayer » la revérifiera.
  }
  return bilanAvecIssues(bilan, issues);
};

// Les annulations à renvoyer appartiennent à la semaine affichée : en changeant de semaine (après
// confirmation), elles ne sont plus à renvoyer d'ici ; leur ligne dit quoi vérifier (voir ligneBilan)
const bilanApresChangementSemaine = (bilan) => {
  if (!bilan) return bilan;
  const issues = {};
  for (const a of annulationsARelancer(bilan)) issues[a.conflit.id] = issueHorsSemaine(bilan, a);
  return bilanAvecIssues(bilan, issues);
};

// Un nouveau bilan s'ajoute au précédent (encore affiché) au lieu de le remplacer : les numéros
// à appeler et les annulations en attente ne sont pas perdus. Un rendez-vous présent dans les
// deux n'apparaît qu'une fois, avec sa nouvelle issue. (Les annulations à renvoyer que l'admin
// vient de redécider ont été réglées avant, par bilanApresDecision.)
const fusionnerBilans = (ancien, nouveau) => {
  if (!ancien) return nouveau;
  const nouveaux = new Set(nouveau.annulations.map((a) => a.conflit.id));
  const garder = (a) => !nouveaux.has(a.conflit.id);
  const issues = {};
  for (const a of ancien.annulations.filter(garder)) {
    if (a.conflit.id in ancien.issues) issues[a.conflit.id] = ancien.issues[a.conflit.id];
  }
  const enAttente = [...ancien.enAttente.filter(garder), ...nouveau.enAttente];
  return {
    annulations: [...ancien.annulations.filter(garder), ...nouveau.annulations],
    issues: { ...issues, ...nouveau.issues },
    enAttente,
    erreurEnvoi: enAttente.length ? nouveau.erreurEnvoi || ancien.erreurEnvoi : "",
  };
};

const lignesDuBilan = (bilan) =>
  (bilan?.annulations || [])
    .filter((a) => a.conflit.id in bilan.issues)
    .map((a) => ligneBilan(a, bilan.issues[a.conflit.id]));

// Message d'une requête d'annulation qui a échoué en entier
const messageErreurEnvoi = (e) => {
  if (e?.response?.status === 429) return "trop de requêtes : patientez quelques minutes avant de réessayer";
  return String(e?.response?.data?.error ?? "").trim() || "erreur réseau ou serveur";
};

// Une requête d'annulation en échec complet a-t-elle pu être traitée quand même ? Oui sans réponse
// HTTP (réseau, délai dépassé : la réponse a pu se perdre) ou sur une erreur 5xx ; non sur une
// 4xx (400, 401, 403, 429…) : le serveur l'a refusée sans rien annuler.
const peutEtreTraite = (e) => {
  const statut = e?.response?.status;
  return !(statut >= 400 && statut < 500);
};

const lienTelephone = (tel) => "tel:" + String(tel ?? "").replace(/[^\d+]/g, "");
// #endregion Fonctions pures

const OPTIONS_ETAT = [
  { valeur: "FERME", libelle: "🚫 Fermé", classe: "etat-ferme" },
  { valeur: "SALON", libelle: "🏛️ Salon", classe: "etat-salon" },
  { valeur: "DOMICILE", libelle: "🏠 Domicile", classe: "etat-domicile" },
];

const calendrier = ref(null);
const lundi = ref(lundiDeLaSemaine(new Date()));
// Le calendrier s'ouvre sur le mois en cours (sinon v-calendar prend le mois du premier
// attribut, c'est-à-dire du lundi : le 1er novembre, il ouvrirait sur octobre).
const pageInitialeCalendrier = { month: new Date().getMonth() + 1, year: new Date().getFullYear() };
const jours = ref([]);
// Planning hebdomadaire et villes : chargés une seule fois (pas à chaque semaine), pour
// rester loin de la limite de requêtes de l'API. null = pas encore chargé.
const planningHebdo = ref(null);
const villesDomicile = ref([]);
const villesChargees = ref(false);
const chargementVilles = ref(false);
const chargement = ref(false);
// Enregistrement des jours et / ou envoi des annulations en cours : tout est verrouillé et
// quitter la page demande confirmation
const enregistrement = ref(false);
const annulationsEnCours = ref(false); // l'enregistrement en cours comprend des annulations
// Relecture des réservations juste avant d'ouvrir la fenêtre des conflits (ou d'enregistrer)
const verification = ref(false);
// Heure de référence des conflits : un rendez-vous déjà commencé n'est plus proposé à
// l'annulation. Mise à jour chaque minute et à chaque relecture des réservations.
const maintenant = ref(new Date());
let minuteur = null;
const erreurChargement = ref(""); // bloquant : la semaine n'a pas pu être affichée
const erreurRechargement = ref(""); // non bloquant : la semaine affichée n'a pas pu être actualisée
const avertissementVilles = ref("");
const erreursSauvegarde = ref({});
const message = ref("");
const erreur = ref("");
const progression = ref(""); // texte du bouton Enregistrer pendant l'enregistrement
// Fenêtre « Rendez-vous en conflit » ouverte (null = fermée) : { mode, aTraiter, groupes }
// mode 'ENREGISTRER' : à l'enregistrement de la semaine ; 'ANNULER' : annulations seules pour
// un jour déjà enregistré (aTraiter vide : aucun jour n'est enregistré).
const modale = ref(null);
const modaleEl = ref(null);
// Bilan des annulations (voir bilanApresEnvoi) : reste affiché jusqu'à ce que l'admin le ferme
// (numéros à appeler, annulations à renvoyer…)
const bilanAnnulations = ref(null);
const lignesBilan = computed(() => lignesDuBilan(bilanAnnulations.value));
// Annulations que « Réessayer » peut renvoyer (toutes de la semaine affichée)
const relances = computed(() => annulationsARelancer(bilanAnnulations.value));
const blocBilan = ref(null);
let focusAvantModale = null;
let jetonChargement = 0; // ignore les réponses d'une semaine qu'on a déjà quittée
// Page quittée : une opération qui attendait une réponse s'arrête là (ni fenêtre, ni
// enregistrement, ni annulation après une relecture des réservations)
let demonte = false;
// Départ confirmé pendant une relecture des réservations (« rien ne sera enregistré ni annulé ») :
// levé tout de suite, car la réponse de la relecture peut arriver AVANT le démontage (le routeur
// attend encore ses gardes globaux) ; la relecture s'arrête alors comme si la page était démontée.
// Remis à zéro au début de chaque relecture (navigation finalement annulée : la page resservira).
let abandonne = false;
const quitte = () => demonte || abandonne;

// La barre d'enregistrement reste collée en bas de l'écran : ses messages doivent pouvoir
// disparaître, sinon ils mangent une grande partie d'un écran de téléphone.
const fermerMessages = () => {
  message.value = "";
  erreur.value = "";
  erreurRechargement.value = "";
};
const afficherSucces = (texte) => {
  message.value = texte;
  setTimeout(() => {
    if (message.value === texte) message.value = "";
  }, 6000);
};

const titre = computed(() => titreSemaine(lundi.value));
const estSemaineCourante = computed(
  () => formatDateISO(lundi.value) === formatDateISO(lundiDeLaSemaine(new Date()))
);
const analyses = computed(() => jours.value.map((jour) => analyserJour(jour, maintenant.value)));
const occupe = computed(() => enregistrement.value || verification.value);
// Saisie verrouillée pendant un enregistrement ET pendant un rechargement : sinon une
// modification (ou un « Annuler ») faite pendant le rechargement serait écrasée à son retour.
// Aussi pendant la relecture des réservations et pendant la fenêtre des conflits (seul le
// motif peut y être changé).
const verrouille = computed(() => occupe.value || chargement.value || !!modale.value);
const nbJoursModifies = computed(() => analyses.value.filter((a) => a.action).length);

const attributsCalendrier = computed(() => {
  const attrs = [{
    key: "semaine",
    highlight: { color: "orange", fillMode: "light" },
    dates: { start: lundi.value, end: ajouterJours(lundi.value, 6) },
  }];
  const datesExceptions = jours.value.filter((j) => j.exception).map((j) => j.date);
  if (datesExceptions.length) attrs.push({ key: "exceptions", dot: { color: "red" }, dates: datesExceptions });
  return attrs;
});

const classeEtat = (jour) => {
  const cible = planningCible(jour);
  if (!cible.actif) return "etat-ferme";
  return cible.mode === "DOMICILE" ? "etat-domicile" : "etat-salon";
};

const libelleAction = (jour, action) => {
  if (action === "SUPPRIMER") return "↩️ Retour au planning normal à l'enregistrement";
  return jour.exception ? "✏️ L'exception sera mise à jour à l'enregistrement" : "🆕 Sera enregistré comme exception";
};

const villesProposees = (jour) =>
  villesDomicile.value.filter(
    (d) => !jour.brouillon.departements.some(
      (v) => v.code_postal === String(d.code_postal) && v.nom === d.nom
    )
  );

// Pendant l'enregistrement, rien ne doit changer à l'écran (les contrôles sont aussi désactivés)
const choisirEtat = (jour, etat) => {
  if (verrouille.value) return;
  jour.brouillon.etat = etat;
  // Passage en Domicile sans aucune ville (ex. exception Salon déjà enregistrée) : on reprend
  // les villes du planning normal de ce jour s'il est en Domicile, plutôt qu'une liste vide.
  if (etat === "DOMICILE" && !jour.brouillon.departements.length && jour.normal?.mode === "DOMICILE") {
    jour.brouillon.departements = normaliserVilles(jour.normal.departements);
  }
};

const ajouterPlage = (jour) => {
  if (verrouille.value) return;
  if (jour.brouillon.plages.length >= MAX_PLAGES) return;
  jour.brouillon.plages.push({ heure_debut: "", heure_fin: "", _cle: ++compteurCle });
};

const retirerPlage = (jour, index) => {
  if (verrouille.value) return;
  jour.brouillon.plages.splice(index, 1);
};

const ajouterVille = (jour) => {
  const dept = villesDomicile.value.find((d) => String(d.id) === jour.villeAAjouter);
  jour.villeAAjouter = "";
  if (verrouille.value) return;
  if (!dept || jour.brouillon.departements.length >= MAX_VILLES) return;
  jour.brouillon.departements = normaliserVilles([
    ...jour.brouillon.departements,
    { code_postal: dept.code_postal, nom: dept.nom },
  ]);
};

const retirerVille = (jour, index) => {
  if (verrouille.value) return;
  jour.brouillon.departements.splice(index, 1);
};

const construireJour = (date, hebdo, exceptionsParDate, reservations, aujourdhui) => {
  const iso = formatDateISO(date);
  const normal = planningPourDate(date, hebdo, null);
  const exception = exceptionsParDate[iso] || null;
  return {
    date,
    iso,
    nom: NOMS_JOURS[jourSemaineBackend(date) - 1],
    passe: date < aujourdhui,
    normal,
    exception,
    reservations: Array.isArray(reservations) ? reservations : [],
    modifie: !!exception,
    brouillon: exception ? brouillonDepuisException(exception, normal) : brouillonDepuisNormal(normal),
    villeAAjouter: "",
  };
};

// Villes proposées en mode Domicile : chargées une fois ; en cas d'échec, simple avertissement
// (on peut toujours enregistrer avec les villes déjà choisies) et bouton Réessayer.
const chargerVilles = async () => {
  if (chargementVilles.value) return;
  chargementVilles.value = true;
  try {
    const { data } = await api.get("/departements");
    if (!Array.isArray(data)) throw new Error("Réponse inattendue de /departements");
    villesDomicile.value = data.filter((d) => d.mode === "DOMICILE");
    villesChargees.value = true;
    avertissementVilles.value = "";
  } catch (e) {
    console.error("Erreur chargement départements:", e);
    avertissementVilles.value = "⚠️ Liste des villes indisponible : impossible d'en ajouter pour le moment.";
  } finally {
    chargementVilles.value = false;
  }
};

// Planning hebdomadaire : une seule requête tant qu'elle a réussi une fois
const chargerPlanningHebdo = async () => {
  if (planningHebdo.value) return planningHebdo.value;
  const { data } = await api.get("/planning-hebdo");
  if (!Array.isArray(data)) throw new Error("Réponse inattendue de /planning-hebdo");
  planningHebdo.value = data;
  return data;
};

// Charge une semaine : 2 requêtes (exceptions + réservations de la période), plus le planning
// hebdomadaire s'il n'a pas encore pu être chargé.
// - Semaine pas encore affichée : en cas d'échec, rien n'est affiché (erreur + Réessayer),
//   donc rien ne peut être enregistré.
// - Semaine déjà affichée (rechargement après un enregistrement, Réessayer) : en cas d'échec,
//   on garde les jours affichés ET leurs brouillons, avec un avertissement non bloquant.
// brouillonsAConserver : { iso: { modifie, brouillon } } à réappliquer sur les jours rechargés
// (jours dont l'enregistrement a échoué, saisie en cours…), pour ne pas perdre la saisie.
const chargerSemaine = async (brouillonsAConserver = {}) => {
  const jeton = ++jetonChargement;
  const dates = joursDeLaSemaine(lundi.value);
  const debut = formatDateISO(dates[0]);
  const fin = formatDateISO(dates[6]);

  chargement.value = true;
  erreurChargement.value = "";
  erreurRechargement.value = "";
  if (jours.value[0]?.iso !== debut) jours.value = [];
  if (!villesChargees.value) chargerVilles(); // indépendant : n'empêche pas d'afficher la semaine

  const [hebdoRes, exceptionsRes, reservationsRes] = await Promise.allSettled([
    chargerPlanningHebdo(),
    getExceptionsAdmin(debut, fin),
    getReservationsPeriode(debut, fin),
  ]);
  // Une autre semaine a été demandée entre-temps, ou la page a été quittée
  if (jeton !== jetonChargement || demonte) return;

  chargement.value = false;

  // Sans les exceptions existantes, on risquerait d'en écraser ou d'en supprimer ; sans les
  // réservations, aucun conflit ne serait signalé (« 0 réservation » serait faux) : on bloque.
  let probleme = "";
  if (exceptionsRes.status !== "fulfilled" || !Array.isArray(exceptionsRes.value)) {
    console.error("Erreur chargement exceptions:", exceptionsRes.reason);
    probleme = "❌ Impossible de charger les exceptions de cette semaine. Rien ne peut être enregistré tant qu'elles ne sont pas chargées.";
  } else if (reservationsRes.status !== "fulfilled" || !Array.isArray(reservationsRes.value)) {
    console.error("Erreur chargement réservations:", reservationsRes.reason);
    probleme = "❌ Impossible de charger les réservations de cette semaine (elles servent à repérer les conflits). Rien ne peut être enregistré tant qu'elles ne sont pas chargées.";
  } else if (hebdoRes.status !== "fulfilled") {
    console.error("Erreur chargement planning hebdo:", hebdoRes.reason);
    probleme = "❌ Impossible de charger le planning hebdomadaire. Réessayez dans un instant.";
  }
  if (probleme) {
    if (jours.value.length) {
      // Cette semaine est déjà affichée : on ne perd ni l'affichage ni les modifications en cours
      erreurRechargement.value = "⚠️ Rechargement impossible : la semaine affichée n'a pas pu être actualisée. Vos modifications non enregistrées sont conservées.";
    } else {
      erreurChargement.value = probleme;
    }
    return;
  }

  const exceptionsParDate = indexerExceptions(exceptionsRes.value);
  const reservationsParJour = grouperReservationsParJour(reservationsRes.value);
  const now = new Date();
  const aujourdhui = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  maintenant.value = now;

  jours.value = dates.map((date) => {
    const iso = formatDateISO(date);
    const jour = construireJour(date, hebdoRes.value, exceptionsParDate, reservationsParJour[iso], aujourdhui);
    const conserve = brouillonsAConserver[jour.iso];
    if (conserve && !jour.passe) {
      jour.modifie = conserve.modifie;
      jour.brouillon = conserve.brouillon;
    }
    return jour;
  });
};

// Copie des brouillons qui ont encore quelque chose à enregistrer
const brouillonsEnAttente = () => {
  const conserves = {};
  jours.value.forEach((jour, i) => {
    if (analyses.value[i]?.action) {
      conserves[jour.iso] = { modifie: jour.modifie, brouillon: JSON.parse(JSON.stringify(jour.brouillon)) };
    }
  });
  return conserves;
};

// « Réessayer » après un rechargement raté : la saisie en cours est réappliquée
const rechargerSemaine = () => {
  if (occupe.value || chargement.value) return;
  return chargerSemaine(brouillonsEnAttente());
};

const confirmerAbandon = () =>
  !nbJoursModifies.value ||
  window.confirm("Des modifications ne sont pas enregistrées. Les abandonner ?");

// Annulations à renvoyer (« Réessayer ») qui ne pourront plus l'être d'ici si l'admin continue
// (fermer le bilan, changer de semaine, quitter la page) : il confirme. question : « … quand même ? »
const confirmerRelancesPerdues = (question) =>
  !relances.value.length || window.confirm(
    `${relances.value.length} annulation(s) n'ont pas pu être envoyées ou ont échoué : ces rendez-vous ne sont peut-être pas annulés. ` +
    `${question} Vous ne pourrez plus les renvoyer d'ici.`
  );

// Renvoie true si la semaine a réellement changé (refus de l'admin, même semaine… → false)
const changerSemaine = (nouveauLundi) => {
  if (occupe.value || modale.value) return false;
  if (formatDateISO(nouveauLundi) === formatDateISO(lundi.value)) return false;
  if (!confirmerRelancesPerdues("Changer de semaine quand même ?")) return false;
  if (!confirmerAbandon()) return false;
  lundi.value = nouveauLundi;
  erreursSauvegarde.value = {};
  fermerMessages();
  // Les annulations à renvoyer appartiennent à la semaine quittée : elles ne sont plus renvoyées
  // d'ici, leur ligne du bilan dit quoi vérifier
  bilanAnnulations.value = bilanApresChangementSemaine(bilanAnnulations.value);
  chargerSemaine();
  return true;
};

// Navigation par boutons : le calendrier suit (changement de mois éventuel)
const deplacerCalendrier = (date) => {
  try {
    Promise.resolve(calendrier.value?.move?.(date)).catch(() => {});
  } catch (e) {
    // Sans conséquence : seul l'affichage du mois n'est pas synchronisé
  }
};

// Le calendrier ne bouge que si la semaine a vraiment changé (sinon il ne montrerait plus
// la semaine affichée, par exemple après un refus d'abandonner des modifications).
const semainePrecedente = () => {
  if (changerSemaine(ajouterJours(lundi.value, -7))) deplacerCalendrier(lundi.value);
};
const semaineSuivante = () => {
  if (changerSemaine(ajouterJours(lundi.value, 7))) deplacerCalendrier(lundi.value);
};
const semaineCourante = () => {
  // comme à l'ouverture : le mois en cours
  if (changerSemaine(lundiDeLaSemaine(new Date()))) deplacerCalendrier(new Date());
};

const onDayClick = (day) => {
  const d = day.date;
  changerSemaine(lundiDeLaSemaine(new Date(d.getFullYear(), d.getMonth(), d.getDate())));
};

const annulerModifications = () => {
  if (verrouille.value) return;
  if (!window.confirm("Annuler les modifications non enregistrées de cette semaine ?")) return;
  for (const jour of jours.value) {
    jour.modifie = !!jour.exception;
    jour.brouillon = jour.exception
      ? brouillonDepuisException(jour.exception, jour.normal)
      : brouillonDepuisNormal(jour.normal);
    jour.villeAAjouter = "";
  }
  erreursSauvegarde.value = {};
};

// Un enregistrement réussi est reflété tout de suite, exactement comme le ferait le rechargement :
// si ce rechargement échoue ensuite, l'écran reste juste (jour enregistré ≠ « à mettre à jour »).
const appliquerEnregistrement = (jour, exception) => {
  jour.exception = exception;
  jour.modifie = !!exception;
  jour.brouillon = exception ? brouillonDepuisException(exception, jour.normal) : brouillonDepuisNormal(jour.normal);
  jour.villeAAjouter = "";
};

// Jours qui ont quelque chose à enregistrer, avec leur analyse (conflits compris)
const joursATraiter = () =>
  jours.value
    .map((jour, i) => ({ jour, analyse: analyses.value[i] }))
    .filter((x) => x.analyse.action);

// Relit les réservations de la semaine affichée juste avant de décider s'il faut ouvrir la
// fenêtre des conflits : un rendez-vous pris depuis l'ouverture de la page y apparaît, un
// rendez-vous déplacé ou annulé entre-temps en disparaît, un rendez-vous qui vient de commencer
// n'y est plus proposé. En cas d'échec : message et false (rien n'est enregistré ni annulé).
// Page quittée, ou départ confirmé, pendant la relecture (voir abandonne) : false, l'opération
// s'arrête là.
// Les analyses (conflits des cartes) se recalculent d'elles-mêmes sur les réservations relues.
const actualiserReservations = async () => {
  const debut = jours.value[0]?.iso;
  const fin = jours.value[jours.value.length - 1]?.iso;
  if (!debut || !fin || demonte) return false;
  abandonne = false;
  verification.value = true;
  progression.value = "⏳ Vérification des réservations…";
  try {
    const data = await getReservationsPeriode(debut, fin);
    if (quitte()) return false;
    if (!Array.isArray(data)) throw new Error("Réponse inattendue de /admin/reservations");
    // Tout est verrouillé pendant la relecture : la semaine affichée n'a pas pu changer
    if (jours.value[0]?.iso !== debut) return false;
    const parJour = grouperReservationsParJour(data);
    for (const jour of jours.value) jour.reservations = parJour[jour.iso] || [];
    maintenant.value = new Date();
    return true;
  } catch (e) {
    if (quitte()) return false;
    console.error("Erreur relecture des réservations:", e);
    erreur.value = "❌ Impossible de vérifier les réservations à jour de la semaine : rien n'a été enregistré ni annulé. Réessayez dans un instant.";
    return false;
  } finally {
    verification.value = false;
    progression.value = "";
    // Départ confirmé mais page toujours là (« Rester sur la page » après beforeunload…)
    // Simple information (pas une erreur) : affichée dans le style neutre/vert de la barre.
    if (abandonne && !demonte) message.value = "ℹ️ Vérification interrompue : rien n'a été enregistré ni annulé.";
  }
};

// Élément qui a le focus (pour l'y ramener à la fermeture de la fenêtre). Relevé AVANT la relecture
// des réservations : pendant celle-ci le bouton est désactivé et le navigateur lui retire le focus.
const elementActif = () => (typeof document !== "undefined" ? document.activeElement : null);

const enregistrerSemaine = async () => {
  if (occupe.value || chargement.value || erreurChargement.value || modale.value) return;
  const focusDepart = elementActif();
  message.value = "";
  erreur.value = "";
  erreursSauvegarde.value = {};

  const enAttente = joursATraiter();
  if (!enAttente.length) {
    afficherSucces("Aucune modification à enregistrer.");
    return;
  }

  const invalides = enAttente.filter((x) => x.analyse.action === "ENREGISTRER" && x.analyse.erreurs.length);
  if (invalides.length) {
    erreur.value = `❌ Corrigez d'abord : ${invalides.map((x) => `${x.jour.nom} ${dateCourte(x.jour.date)}`).join(", ")}.`;
    return;
  }

  // Les conflits sont recalculés sur les réservations relues à l'instant. S'il en reste, l'admin
  // choisit dans la fenêtre de les annuler (clients prévenus) ou de les garder.
  // Page quittée (ou départ confirmé) pendant la relecture : rien n'est ouvert ni enregistré.
  if (!(await actualiserReservations()) || quitte()) return;
  const aTraiter = joursATraiter();
  const groupes = groupesConflits(aTraiter);
  if (groupes.length) {
    ouvrirModale({ mode: "ENREGISTRER", aTraiter, groupes }, focusDepart);
    return;
  }
  await executerEnregistrement(aTraiter, []);
};

// Bouton de la carte d'un jour sans rien à enregistrer (exception déjà enregistrée) qui garde des
// rendez-vous en conflit (« Enregistrer sans annuler », case décochée, annulation ratée…) : même
// fenêtre, limitée à ce jour, pour les annuler sans rien enregistrer.
const ouvrirAnnulationsDuJour = async (jour) => {
  if (occupe.value || chargement.value || erreurChargement.value || modale.value) return;
  const focusDepart = elementActif();
  message.value = "";
  erreur.value = "";
  if (!(await actualiserReservations()) || quitte()) return;
  const analyse = analyses.value[jours.value.indexOf(jour)];
  if (!analyse || analyse.action) return;
  const groupes = groupesConflits([{ jour, analyse }], true);
  if (!groupes.length) {
    afficherSucces(`ℹ️ ${jour.nom} ${dateCourte(jour.date)} : plus aucun rendez-vous en conflit (déplacé, annulé ou déjà commencé entre-temps).`);
    return;
  }
  ouvrirModale({ mode: "ANNULER", aTraiter: [], groupes }, focusDepart);
};

// --- Fenêtre « Rendez-vous en conflit » ---
const nbCoches = computed(() =>
  modale.value ? modale.value.groupes.reduce((n, g) => n + g.conflits.filter((c) => c.coche).length, 0) : 0
);

// Pas de défilement de la page derrière la fenêtre (gênant sur téléphone)
const bloquerDefilement = (bloquer) => {
  if (typeof document === "undefined" || !document.body) return;
  document.body.style.overflow = bloquer ? "hidden" : "";
};

// Échap = Retour ; Tab reste dans la fenêtre. Écouté sur window : Échap fonctionne même si le
// focus est sorti de la fenêtre (clic sur le fond).
const clavierModale = (e) => {
  if (!modale.value || enregistrement.value) return;
  if (e.key === "Escape" || e.key === "Esc") {
    e.preventDefault();
    fermerModale();
    return;
  }
  const el = modaleEl.value;
  if (e.key !== "Tab" || !el || typeof document === "undefined") return;
  const focusables = [...el.querySelectorAll("button:not([disabled]), input:not([disabled]), a[href]")];
  if (!focusables.length) return;
  const premier = focusables[0];
  const dernier = focusables[focusables.length - 1];
  const actif = document.activeElement;
  const dedans = el.contains(actif) && actif !== el;
  if (e.shiftKey && (!dedans || actif === premier)) {
    e.preventDefault();
    dernier.focus();
  } else if (!e.shiftKey && (!dedans || actif === dernier)) {
    e.preventDefault();
    premier.focus();
  }
};

// contenu : { mode, aTraiter, groupes } (voir modale) ; focusPrecedent : où ramener le focus
const ouvrirModale = (contenu, focusPrecedent = elementActif()) => {
  if (quitte()) return; // jamais d'écouteur ni de défilement bloqué pour une page quittée
  focusAvantModale = focusPrecedent;
  modale.value = contenu;
  window.addEventListener("keydown", clavierModale);
  bloquerDefilement(true);
  // Focus sur la fenêtre elle-même (pas sur un champ : pas de clavier qui surgit sur téléphone)
  nextTick(() => modaleEl.value?.focus?.());
};

const fermerModale = () => {
  if (!modale.value) return;
  modale.value = null;
  window.removeEventListener("keydown", clavierModale);
  bloquerDefilement(false);
  const precedent = focusAvantModale;
  focusAvantModale = null;
  nextTick(() => precedent?.focus?.());
};

// « Retour » : rien n'est enregistré (un motif modifié dans la fenêtre reste dans le jour)
const retourModale = () => {
  if (enregistrement.value) return;
  fermerModale();
};

// annuler = true : (enregistrer puis) annuler les rendez-vous cochés ; false : enregistrer
// seulement (pas proposé en mode 'ANNULER', où rien n'est enregistré)
const confirmerModale = (annuler) => {
  const m = modale.value;
  if (!m || occupe.value) return;
  if (m.mode === "ANNULER" && !annuler) return;
  if (annuler && !nbCoches.value) return;
  // Motifs relevés maintenant : l'enregistrement d'un jour réinitialise son brouillon
  const annulations = annuler ? annulationsCochees(m.groupes) : [];
  // Rendez-vous montrés dans la fenêtre : une annulation encore à renvoyer pour l'un d'eux est
  // redécidée ici (cochée : renvoyée avec la nouvelle décision ; sinon : gardée)
  const montres = new Set(m.groupes.flatMap((g) => g.conflits.map((c) => c.id)));
  fermerModale();
  return executerEnregistrement(m.aTraiter, annulations, montres);
};

// Envoie les annulations en une requête (plusieurs seulement au-delà de 100). Si une requête
// échoue en entier (réseau, 429, 500…), on s'arrête là : ses annulations et les suivantes restent
// à renvoyer telles quelles. Renvoie { resultats: { id: résultat }, restants, message } ; les
// restants de la requête en échec sont marqués incertain si elle a pu être traitée (peutEtreTraite),
// les suivants n'ont pas été envoyés.
const envoyerAnnulations = async (annulations) => {
  const resultats = {};
  const paquets = lots(annulations, MAX_ANNULATIONS_PAR_APPEL);
  for (let i = 0; i < paquets.length; i++) {
    try {
      const reponse = await annulerReservations(paquets[i].map((a) => ({ ...a.requete })));
      for (const r of Array.isArray(reponse?.resultats) ? reponse.resultats : []) {
        if (r && r.id != null) resultats[r.id] = r;
      }
    } catch (e) {
      const incertain = peutEtreTraite(e);
      return {
        resultats,
        restants: [...paquets[i].map((a) => ({ ...a, incertain })), ...paquets.slice(i + 1).flat()],
        message: messageErreurEnvoi(e),
      };
    }
  }
  return { resultats, restants: [], message: "" };
};

const montrerBilan = async () => {
  await nextTick();
  if (demonte) return;
  blocBilan.value?.scrollIntoView?.({ behavior: "smooth", block: "start" });
  blocBilan.value?.focus?.({ preventScroll: true });
};

// Messages courts de la barre. issues : issues des annulations de CETTE opération ;
// nbEnAttente : celles dont l'envoi a échoué en entier (à réessayer depuis le bilan).
const afficherResultat = (reussis, nbEchecsJours, issues, nbEnAttente) => {
  const nbAnnules = issues.filter((x) => x?.statut === "annulee").length;
  const nbNonAnnules = nbEnAttente + issues.filter(estEchec).length;
  const annules = nbAnnules ? `${nbAnnules} rendez-vous annulé(s)` : "";
  if (reussis) afficherSucces(`✅ ${reussis} jour(s) mis à jour` + (annules ? ` · ${annules}` : ""));
  else if (annules) afficherSucces(`✅ ${annules}`);
  const erreurs = [];
  if (nbEchecsJours) erreurs.push(`❌ ${nbEchecsJours} jour(s) non enregistré(s) : voir le détail sur chaque jour.`);
  if (nbNonAnnules) erreurs.push(`❌ ${nbNonAnnules} rendez-vous non annulé(s) : voir le bilan des annulations.`);
  erreur.value = erreurs.join(" ");
};

// Enregistre les jours un par un, puis envoie EN UNE REQUÊTE les annulations choisies des jours
// bien enregistrés, puis recharge la semaine. Tout reste verrouillé du début à la fin.
// aTraiter vide (mode 'ANNULER') : annulations seules, aucun jour enregistré.
// montres : ids des rendez-vous montrés dans la fenêtre des conflits (voir bilanApresDecision).
// Une fois commencé, l'enregistrement va jusqu'au bout même si la page est quittée (c'est ce
// qu'annonce la confirmation pour partir) ; seuls le rechargement et l'affichage sont sautés.
// Jamais commencé après un départ confirmé pendant la relecture qui le précède (voir abandonne).
const executerEnregistrement = async (aTraiter, annulations, montres = new Set()) => {
  if (quitte()) return;
  enregistrement.value = true;
  annulationsEnCours.value = annulations.length > 0;
  progression.value = "";
  let reussis = 0;
  const echecs = {};
  let bilan = null;

  try {
    // Appels un par un : un échec n'empêche pas les autres jours d'être enregistrés
    for (const { jour, analyse } of aTraiter) {
      try {
        if (analyse.action === "ENREGISTRER") {
          const payload = construirePayload(jour.brouillon);
          const reponse = await enregistrerException(jour.iso, payload);
          appliquerEnregistrement(jour, exceptionEnregistree(jour.iso, payload, reponse));
        } else {
          await supprimerException(jour.iso);
          appliquerEnregistrement(jour, null);
        }
        reussis++;
      } catch (e) {
        // Suppression d'une exception déjà absente : le jour est bien revenu au planning normal
        if (analyse.action === "SUPPRIMER" && e.response?.status === 404) {
          appliquerEnregistrement(jour, null);
          reussis++;
          continue;
        }
        console.error(`Erreur enregistrement ${jour.iso}:`, e);
        echecs[jour.iso] = e.response?.data?.error || "Erreur lors de l'enregistrement, réessayez.";
      }
    }

    // Annulations encore à renvoyer (bilan précédent) que cette opération redécide : jours qu'on
    // vient d'enregistrer, rendez-vous montrés dans la fenêtre. Jamais renvoyées telles quelles.
    const ancien = bilanApresDecision(bilanAnnulations.value, {
      montres,
      coches: new Set(annulations.map((a) => a.conflit.id)),
      isosTouches: new Set(aTraiter.map((x) => x.jour.iso)),
      isosEchec: new Set(Object.keys(echecs)),
      jours: jours.value,
      maintenant: maintenant.value,
    });
    bilanAnnulations.value = ancien;

    // Jamais d'annulation pour un jour dont l'enregistrement a échoué (son planning n'a pas
    // changé : le rendez-vous y est toujours valable)
    if (annulations.length) {
      const issues = {};
      for (const a of annulations) if (echecs[a.iso]) issues[a.conflit.id] = { nonTraite: true };
      const aEnvoyer = annulations.filter((a) => !echecs[a.iso]);
      bilan = { annulations, issues, enAttente: [], erreurEnvoi: "" };
      if (aEnvoyer.length) {
        progression.value = `⏳ Annulation de ${aEnvoyer.length} rendez-vous…`;
        bilan = bilanApresEnvoi(bilan, aEnvoyer, await envoyerAnnulations(aEnvoyer));
      }
      // Affiché tout de suite : un rechargement qui échouerait ne doit pas le faire perdre
      bilanAnnulations.value = fusionnerBilans(ancien, bilan);
    }
    progression.value = "";

    // Si ce rechargement échoue, la semaine affichée est gardée, avec la saisie encore en attente
    // (jours en échec ; en mode 'ANNULER', jours modifiés mais pas encore enregistrés)
    if (!demonte) await chargerSemaine(brouillonsEnAttente());
  } finally {
    enregistrement.value = false;
    annulationsEnCours.value = false;
    progression.value = "";
  }
  if (demonte) return;

  erreursSauvegarde.value = echecs;
  afficherResultat(reussis, Object.keys(echecs).length,
    bilan ? Object.values(bilan.issues) : [], bilan ? bilan.enAttente.length : 0);
  if (bilan) await montrerBilan();
};

// « Réessayer » du bilan (annulations en attente et échecs du backend) : jamais de charge utile
// périmée. Les réservations de la semaine affichée sont d'abord relues ; chaque annulation est
// revérifiée (trierRelances) : seules celles encore en conflit avec le planning en vigueur de
// leur jour sont renvoyées, avec le code de conflit et le motif actuels. Puis la semaine est
// rechargée.
const relancerAnnulations = async () => {
  if (!bilanAnnulations.value || !relances.value.length || verrouille.value || erreurChargement.value) return;
  message.value = "";
  erreur.value = "";
  // Page quittée (ou départ confirmé) pendant la relecture : rien n'est envoyé
  if (!(await actualiserReservations()) || quitte()) return;

  const tri = trierRelances(bilanAnnulations.value, jours.value, maintenant.value);
  let bilan = bilanAvecIssues(bilanAnnulations.value, tri.issues);
  bilanAnnulations.value = bilan;
  let restants = [];
  if (tri.aEnvoyer.length) {
    enregistrement.value = true;
    annulationsEnCours.value = true;
    progression.value = `⏳ Annulation de ${tri.aEnvoyer.length} rendez-vous…`;
    try {
      const envoi = await envoyerAnnulations(tri.aEnvoyer);
      restants = envoi.restants;
      bilan = bilanApresEnvoi(bilan, tri.aEnvoyer, envoi, tri.incertaines);
      bilanAnnulations.value = bilan;
      progression.value = "";
      if (!demonte && restants.length < tri.aEnvoyer.length) await chargerSemaine(brouillonsEnAttente());
    } finally {
      enregistrement.value = false;
      annulationsEnCours.value = false;
      progression.value = "";
    }
  }
  if (demonte) return;

  const restantsIds = new Set(restants.map((a) => a.conflit.id));
  const traitees = [...tri.aEnvoyer.map((a) => a.conflit.id).filter((id) => !restantsIds.has(id)), ...Object.keys(tri.issues)];
  afficherResultat(0, 0, traitees.map((id) => bilan.issues[id]), restants.length);
  if (tri.gardees.length) {
    erreur.value = [erreur.value,
      `⏸️ ${tri.gardees.length} annulation(s) pas renvoyée(s) : leur jour a des modifications non enregistrées. ` +
      "Enregistrez la semaine (la fenêtre des conflits reproposera ces rendez-vous s'ils sont encore en conflit) ou annulez ces modifications, puis réessayez.",
    ].filter(Boolean).join(" ");
  } else if (!message.value && !erreur.value) {
    afficherSucces("ℹ️ Réessai terminé : voir le bilan des annulations.");
  }
  await montrerBilan();
};

const fermerBilan = () => {
  if (!bilanAnnulations.value || occupe.value) return;
  if (!confirmerRelancesPerdues("Fermer le bilan quand même ?")) return;
  bilanAnnulations.value = null;
};

// ⚠️ Quitter la page : pendant un enregistrement (annulations comprises), pendant la relecture des
// réservations qui le précède, avec des annulations à renvoyer, ou avec des modifications non
// enregistrées
const texteQuitterPendantEnregistrement = () =>
  annulationsEnCours.value
    ? "Annulations en cours… Si vous quittez la page maintenant, vous ne verrez pas leur bilan (qui a été prévenu par e-mail, qui appeler). Quitter quand même ?"
    : "Enregistrement en cours… Si vous quittez la page maintenant, vous ne verrez pas son résultat. Quitter quand même ?";
// Pendant la relecture, rien n'est encore envoyé : quitter l'abandonne (voir abandonne)
const texteQuitterPendantVerification = () =>
  "Vérification des réservations en cours… Si vous quittez la page maintenant, rien ne sera enregistré ni annulé" +
  (nbJoursModifies.value ? " et vos modifications non enregistrées seront perdues" : "") +
  ". Quitter quand même ?";

onBeforeRouteLeave(() => {
  if (enregistrement.value) {
    if (!window.confirm(texteQuitterPendantEnregistrement())) return false;
    return;
  }
  if (verification.value) {
    if (!window.confirm(texteQuitterPendantVerification())) return false;
    abandonne = true; // tout de suite : la réponse de la relecture peut arriver avant le démontage
    return;
  }
  if (!confirmerRelancesPerdues("Quitter la page quand même ?")) return false;
  if (!confirmerAbandon()) return false;
});

const avantDechargement = (e) => {
  // Relecture en cours : abandonnée dès maintenant (le navigateur ne dit pas si l'admin part ou
  // reste ; s'il reste, il recommence simplement : rien n'a été enregistré ni annulé)
  if (verification.value) abandonne = true;
  if (enregistrement.value || verification.value || relances.value.length || nbJoursModifies.value) {
    e.preventDefault();
    e.returnValue = "";
  }
};

onMounted(() => {
  window.addEventListener("beforeunload", avantDechargement);
  minuteur = setInterval(() => { maintenant.value = new Date(); }, 60 * 1000);
  chargerSemaine();
});

onBeforeUnmount(() => {
  demonte = true; // les opérations en attente d'une réponse s'arrêtent (voir demonte)
  window.removeEventListener("beforeunload", avantDechargement);
  window.removeEventListener("keydown", clavierModale);
  clearInterval(minuteur);
  // Toujours : la page suivante ne doit jamais hériter d'un défilement bloqué
  bloquerDefilement(false);
});
</script>

<style scoped>
.admin-semaine {
  padding: 30px 20px 0;
  max-width: 1400px;
  margin: auto;
  background-color: #f8f3e7;
  min-height: 100vh;
  font-family: 'Segoe UI', sans-serif;
  color: #3e2723;
}

.back-btn {
  display: inline-block;
  margin-bottom: 20px;
  padding: 8px 14px;
  background-color: #d4a373;
  color: white;
  text-decoration: none;
  border-radius: 6px;
  font-weight: 600;
  transition: background-color 0.2s;
}

.back-btn:hover {
  background-color: #c78d56;
}

h1 {
  font-size: 2rem;
  color: #5a3d2b;
  margin-bottom: 20px;
  text-align: center;
}

/* ℹ️ Explication */
.explication {
  background: white;
  border-left: 4px solid #d4a373;
  border-radius: 8px;
  padding: 15px 20px;
  margin-bottom: 25px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.04);
}

.explication p {
  margin: 0 0 8px;
}

.explication ul {
  margin: 0;
  padding-left: 0;
  list-style: none;
}

.explication li {
  margin: 4px 0;
}

/* 🗓️ Choix de la semaine */
.semaine-selection {
  display: grid;
  grid-template-columns: minmax(260px, 360px) 1fr;
  gap: 20px;
  align-items: center;
  margin-bottom: 25px;
}

.calendar-card {
  background: white;
  padding: 15px;
  border-radius: 10px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.05);
}

.calendar-hint {
  margin: 8px 0 0;
  font-size: 0.85rem;
  color: #8d6e63;
  text-align: center;
}

.semaine-nav {
  text-align: center;
}

.semaine-nav h2 {
  font-size: 1.5rem;
  color: #5a3d2b;
  margin: 0 0 15px;
}

.nav-buttons {
  display: flex;
  gap: 10px;
  justify-content: center;
  flex-wrap: wrap;
}

.nav-btn {
  padding: 10px 16px;
  background-color: #fff;
  border: 2px solid #d4a373;
  color: #5a3d2b;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 600;
  font-size: 0.95rem;
  transition: all 0.2s;
}

.nav-btn:hover:not(:disabled) {
  background-color: #d4a373;
  color: white;
}

.lien-btn {
  margin-top: 12px;
  background: none;
  border: none;
  color: #5a3d2b;
  text-decoration: underline;
  cursor: pointer;
  font-size: 0.9rem;
}

button:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

/* 📅 Jours */
/* Le fieldset ne sert qu'à tout désactiver pendant l'enregistrement : aucun style propre.
   min-width: 0, sinon un fieldset s'élargit à son contenu (débordement sur téléphone). */
.jours-fieldset {
  border: 0;
  margin: 0;
  padding: 0;
  min-width: 0;
}

/* minmax(min(310px, 100%), …) : jamais plus large que l'écran, même très étroit */
.jours-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(min(310px, 100%), 1fr));
  gap: 18px;
  transition: opacity 0.2s;
}

.jours-grid.rechargement {
  opacity: 0.5;
  pointer-events: none;
}

.jour-card {
  min-width: 0;
  background: white;
  border-radius: 12px;
  padding: 18px;
  border-left: 6px solid #d4a373;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.05);
}

.jour-card.etat-salon { border-left-color: #d4a373; }
.jour-card.etat-domicile { border-left-color: #6a994e; }
.jour-card.etat-ferme { border-left-color: #e76f51; }

.jour-card.avec-exception {
  background-color: #fffaf0;
  box-shadow: 0 4px 12px rgba(212, 163, 115, 0.25);
}

.jour-card.passe {
  opacity: 0.65;
}

.jour-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 8px;
  flex-wrap: wrap;
  margin-bottom: 8px;
}

.jour-header h3 {
  margin: 0;
  font-size: 1.25rem;
  color: #5a3d2b;
}

.jour-date {
  font-weight: 400;
  color: #8d6e63;
  font-size: 1rem;
}

.badges {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
}

.badge {
  font-size: 0.75rem;
  font-weight: 700;
  padding: 3px 8px;
  border-radius: 12px;
  white-space: nowrap;
}

.badge-exception {
  background-color: #d4a373;
  color: white;
}

.badge-passe {
  background-color: #e0e0e0;
  color: #616161;
}

.motif-enregistre {
  margin: 0 0 8px;
  font-style: italic;
  color: #6d4c41;
  font-size: 0.9rem;
}

.planning-normal,
.nb-resa {
  margin: 6px 0;
  font-size: 0.92rem;
}

.nb-resa {
  color: #6d4c41;
}

/* Interrupteur */
.toggle {
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 14px 0 6px;
  cursor: pointer;
  font-weight: 600;
  user-select: none;
}

.toggle input {
  position: absolute;
  opacity: 0;
  width: 0;
  height: 0;
}

.toggle-slider {
  position: relative;
  flex-shrink: 0;
  width: 46px;
  height: 26px;
  background-color: #ccc;
  border-radius: 13px;
  transition: background-color 0.2s;
}

.toggle-slider::after {
  content: "";
  position: absolute;
  top: 3px;
  left: 3px;
  width: 20px;
  height: 20px;
  background: white;
  border-radius: 50%;
  transition: transform 0.2s;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
}

.toggle input:checked + .toggle-slider {
  background-color: #d4a373;
}

.toggle input:checked + .toggle-slider::after {
  transform: translateX(20px);
}

.toggle input:focus-visible + .toggle-slider {
  outline: 2px solid #5a3d2b;
  outline-offset: 2px;
}

.toggle input:disabled + .toggle-slider {
  opacity: 0.55;
}

.info-retour {
  margin: 8px 0 0;
  padding: 10px 12px;
  background-color: #e8f5e9;
  border-radius: 6px;
  font-size: 0.9rem;
}

/* ✏️ Éditeur */
.editeur {
  margin-top: 12px;
  padding: 14px;
  background-color: #fdf9f1;
  border-radius: 8px;
}

.etat-selector {
  display: flex;
  gap: 8px;
  margin-bottom: 12px;
}

.etat-btn {
  flex: 1;
  padding: 10px 6px;
  background-color: #eee;
  border: 2px solid #ccc;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 600;
  font-size: 0.9rem;
  transition: all 0.2s;
}

.etat-btn.active.etat-ferme { background-color: #e76f51; border-color: #e76f51; color: white; }
.etat-btn.active.etat-salon { background-color: #d4a373; border-color: #d4a373; color: white; }
.etat-btn.active.etat-domicile { background-color: #6a994e; border-color: #6a994e; color: white; }

.bloc {
  margin-bottom: 12px;
}

.bloc h4 {
  margin: 0 0 8px;
  font-size: 1rem;
  color: #5a3d2b;
}

.plage-ligne {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
}

/* width: 0 + flex: 1 1 0 : les champs heure se partagent la place disponible au lieu
   d'imposer leur largeur naturelle (qui faisait déborder la carte sur téléphone) */
.plage-ligne input {
  flex: 1 1 0;
  min-width: 0;
  width: 0;
  padding: 8px;
  border: 1px solid #ccc;
  border-radius: 5px;
  font-size: 1rem;
}

.fleche {
  color: #8d6e63;
}

.villes-list {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 8px;
}

.ville-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 6px 5px 10px;
  background-color: #e3efd9;
  color: #2f4a1f;
  border-radius: 16px;
  font-size: 0.88rem;
}

.chip-remove {
  border: none;
  background-color: #6a994e;
  color: white;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  cursor: pointer;
  font-size: 0.75rem;
  line-height: 1;
}

.ville-select,
.motif input {
  width: 100%;
  padding: 9px;
  border: 1px solid #ccc;
  border-radius: 5px;
  font-size: 0.95rem;
  box-sizing: border-box;
  background: white;
}

.motif {
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-weight: 600;
  font-size: 0.9rem;
}

.motif-aide {
  font-weight: 400;
  font-size: 0.8rem;
  color: #8d6e63;
}

.info-identique {
  margin: 10px 0 0;
  font-size: 0.85rem;
  color: #6d4c41;
}

.erreurs-jour {
  list-style: none;
  margin: 10px 0 0;
  padding: 10px 12px;
  background-color: #ffebee;
  border-radius: 6px;
  color: #c62828;
  font-size: 0.88rem;
}

.erreurs-jour li + li {
  margin-top: 4px;
}

/* ⚠️ Conflits */
.conflits {
  margin-top: 12px;
  padding: 10px 12px;
  background-color: #fff3e0;
  border-left: 4px solid #e76f51;
  border-radius: 6px;
  font-size: 0.88rem;
}

.conflits ul {
  margin: 6px 0;
  padding-left: 18px;
}

.conflits p {
  margin: 0;
}

.conflits-btn {
  margin-top: 8px;
  width: 100%;
  background-color: #c62828;
  color: white;
  white-space: normal;
}

/* Pleine largeur : pas d'agrandissement au survol (il ferait déborder la carte) */
.conflits-btn:hover:not(:disabled) {
  transform: none;
  opacity: 0.92;
}

.statut-action {
  margin: 12px 0 0;
  font-size: 0.88rem;
  font-weight: 600;
  color: #5a3d2b;
}

.erreur-sauvegarde {
  margin: 8px 0 0;
  font-size: 0.88rem;
  font-weight: 600;
  color: #c62828;
}

/* Boutons */
.action-btn {
  padding: 9px 14px;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 600;
  font-size: 0.92rem;
  transition: opacity 0.2s, transform 0.2s ease;
}

.action-btn:hover:not(:disabled) {
  transform: scale(1.03);
}

.add-btn {
  background-color: #5a3d2b;
  color: white;
}

.icon-btn {
  flex-shrink: 0;
  width: 38px;
  height: 38px;
  border: none;
  border-radius: 6px;
  cursor: pointer;
}

.delete-btn {
  background-color: #e76f51;
  color: white;
}

.save-btn {
  background-color: #6a994e;
  color: white;
  font-size: 1rem;
  padding: 12px 20px;
}

.cancel-btn {
  background-color: #fff;
  color: #5a3d2b;
  border: 2px solid #d4a373;
}

.retry-btn {
  margin-top: 10px;
  background-color: #d4a373;
  color: white;
}

/* 💾 Barre d'enregistrement (reste visible en bas sur téléphone) */
.barre-enregistrement {
  position: sticky;
  bottom: 0;
  z-index: 5;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  margin-top: 25px;
  padding: 14px 18px;
  background: white;
  border-top: 3px solid #d4a373;
  border-radius: 12px 12px 0 0;
  box-shadow: 0 -4px 12px rgba(0, 0, 0, 0.08);
}

.resume-modifs {
  font-weight: 600;
  color: #5a3d2b;
}

.barre-actions {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}

/* Retour de l'enregistrement, au-dessus des boutons de la barre */
.barre-messages {
  flex-basis: 100%;
  display: flex;
  flex-direction: column;
  gap: 8px;
  position: relative;
  padding-right: 32px; /* place pour le bouton ✕ */
  max-height: 30vh; /* la barre est collée en bas : elle ne doit pas cacher les jours */
  overflow-y: auto;
}

.fermer-messages {
  position: absolute;
  top: 0;
  right: 0;
  width: 28px;
  height: 28px;
  border: none;
  border-radius: 50%;
  background: #eee;
  color: #5a3d2b;
  cursor: pointer;
  font-weight: 700;
}

.barre-messages .success-message,
.barre-messages .error-message,
.barre-messages .warning-message {
  margin: 0;
  padding: 10px 12px;
}

/* Messages */
.loading {
  text-align: center;
  padding: 30px;
  color: #6d4c41;
  font-weight: 600;
}

.success-message,
.error-message,
.warning-message {
  padding: 15px;
  margin: 20px 0;
  border-radius: 6px;
  font-weight: 600;
  text-align: center;
}

.error-message p,
.warning-message p {
  margin: 0;
}

.success-message {
  background-color: #c8e6c9;
  color: #2e7d32;
}

.error-message {
  background-color: #ffcdd2;
  color: #c62828;
}

.warning-message {
  background-color: #fff3e0;
  color: #8d4b00;
}

.no-data {
  color: #999;
  font-style: italic;
  margin: 4px 0 8px;
  font-size: 0.9rem;
}

/* 📋 Bilan des annulations */
.bilan-annulations {
  position: relative;
  margin-bottom: 20px;
  padding: 16px 48px 16px 18px;
  background: white;
  border-left: 6px solid #d4a373;
  border-radius: 10px;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.06);
  scroll-margin-top: 12px;
}

.bilan-annulations:focus {
  outline: none;
}

.bilan-annulations:focus-visible {
  outline: 2px solid #5a3d2b;
  outline-offset: 2px;
}

.bilan-annulations .fermer-messages {
  top: 12px;
  right: 12px;
}

.bilan-annulations h2 {
  margin: 0 0 10px;
  font-size: 1.15rem;
  color: #5a3d2b;
}

.bilan-annulations ul {
  list-style: none;
  margin: 0;
  padding: 0;
}

.bilan-annulations li {
  padding: 8px 10px;
  border-radius: 6px;
  font-size: 0.92rem;
  overflow-wrap: anywhere;
}

.bilan-annulations li + li {
  margin-top: 6px;
}

.bilan-annulations a {
  color: #5a3d2b;
  font-weight: 700;
  white-space: nowrap;
}

.bilan-email { background-color: #e8f5e9; }
.bilan-appeler,
.bilan-inconnu,
.bilan-deplacee,
.bilan-probable,
.bilan-absent,
.bilan-hors_semaine,
.bilan-non_traite { background-color: #fff3e0; }
.bilan-deja,
.bilan-plus_en_conflit,
.bilan-garde,
.bilan-passee { background-color: #f5f5f5; }
.bilan-echec { background-color: #ffebee; color: #c62828; }

/* Envoi des annulations en échec : une ligne + Réessayer, jusqu'à réussite ou fermeture */
.bilan-envoi-echec {
  margin-bottom: 10px;
  padding: 10px 12px;
  border-radius: 6px;
  background-color: #ffebee;
  color: #c62828;
  font-weight: 600;
  font-size: 0.92rem;
  overflow-wrap: anywhere;
}

.bilan-envoi-echec p {
  margin: 0;
}

.bilan-envoi-echec p + p {
  margin-top: 4px;
}

.bilan-envoi-echec .bilan-relance-aide {
  font-weight: 400;
  font-size: 0.85rem;
  color: #5a3d2b;
}

.bilan-envoi-echec .retry-btn {
  margin-top: 8px;
}

/* ⚠️ Fenêtre « Rendez-vous en conflit » */
.modale-fond {
  position: fixed;
  inset: 0;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  background-color: rgba(62, 39, 35, 0.55);
}

.modale {
  width: 100%;
  max-width: 620px;
  max-height: calc(100vh - 32px);
  max-height: calc(100dvh - 32px);
  overflow-y: auto;
  overscroll-behavior: contain;
  box-sizing: border-box;
  padding: 22px;
  background: white;
  border-top: 6px solid #e76f51;
  border-radius: 12px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.25);
  color: #3e2723;
  font-family: 'Segoe UI', sans-serif;
}

.modale:focus {
  outline: none;
}

.modale h2 {
  margin: 0 0 8px;
  font-size: 1.3rem;
  color: #5a3d2b;
}

.modale-explication {
  margin: 0 0 14px;
  font-size: 0.95rem;
}

.modale-jour {
  margin-bottom: 12px;
  padding: 12px 14px;
  background-color: #fdf9f1;
  border-left: 4px solid #d4a373;
  border-radius: 8px;
}

.modale-jour h3 {
  margin: 0 0 8px;
  font-size: 1.05rem;
  color: #5a3d2b;
}

.modale-motif {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-bottom: 10px;
  font-size: 0.9rem;
}

.modale-motif label {
  font-weight: 600;
}

.modale-motif input {
  width: 100%;
  padding: 9px;
  border: 1px solid #ccc;
  border-radius: 5px;
  font-size: 1rem;
  box-sizing: border-box;
  background: white;
}

.modale-motif small,
.modale-raison {
  font-size: 0.8rem;
  color: #8d6e63;
}

.modale-resas {
  list-style: none;
  margin: 0;
  padding: 0;
}

.modale-resas li + li {
  margin-top: 6px;
}

.modale-resa {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 8px 10px;
  background: white;
  border: 1px solid #ecdcc8;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.92rem;
}

.modale-resa input {
  flex-shrink: 0;
  width: 20px;
  height: 20px;
  margin: 1px 0 0;
  accent-color: #c62828;
  cursor: pointer;
}

.modale-resa > span {
  min-width: 0;
  overflow-wrap: anywhere;
}

.modale-raison {
  display: block;
  margin-top: 2px;
}

.modale-actions {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 16px;
}

.modale-actions .action-btn {
  width: 100%;
  padding: 12px 14px;
  font-size: 1rem;
  text-align: center;
}

/* Boutons pleine largeur : pas d'agrandissement au survol (il ferait déborder la fenêtre) */
.modale-actions .action-btn:hover:not(:disabled) {
  transform: none;
  opacity: 0.92;
}

.modale-actions small {
  display: block;
  margin-top: 3px;
  font-weight: 400;
  font-size: 0.8rem;
}

.modale-principal {
  background-color: #c62828;
  color: white;
}

.modale-secondaire {
  background-color: white;
  color: #5a3d2b;
  border: 2px solid #d4a373;
}

.modale-retour {
  background: transparent;
  color: #5a3d2b;
  text-decoration: underline;
}

.modale button:focus-visible,
.modale input:focus-visible {
  outline: 2px solid #5a3d2b;
  outline-offset: 2px;
}

@media (max-width: 768px) {
  .admin-semaine {
    padding: 20px 12px 0;
  }

  h1 {
    font-size: 1.6rem;
  }

  .semaine-selection {
    grid-template-columns: 1fr;
  }

  .semaine-nav h2 {
    font-size: 1.2rem;
  }

  .nav-btn {
    flex: 1;
  }

  .jours-grid {
    grid-template-columns: minmax(0, 1fr);
  }

  .barre-enregistrement {
    margin-left: -12px;
    margin-right: -12px;
    border-radius: 0;
  }

  .barre-actions {
    width: 100%;
  }

  .barre-actions .save-btn {
    flex: 1;
  }
}

/* Très petits écrans (360px) : un peu plus de place pour lire l'heure en entier */
@media (max-width: 400px) {
  .modale-fond {
    padding: 8px;
  }

  .modale {
    max-height: calc(100vh - 16px);
    max-height: calc(100dvh - 16px);
    padding: 16px 12px;
  }

  .modale-jour {
    padding: 10px;
  }

  .bilan-annulations {
    padding: 14px 44px 14px 12px;
  }

  .editeur {
    padding: 14px 10px;
  }

  .plage-ligne {
    gap: 6px;
  }

  .plage-ligne input {
    padding: 8px 4px;
  }
}
</style>
